<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <div class="page-head">
              <h2>IABF Accounting</h2>
              <div class="head-actions">
                <label class="year-label">
                  <span>Festival year</span>
                  <select v-model.number="activeYear" class="year-select">
                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                  </select>
                </label>
                <v-btn small color="primary" @click="openNew">+ Add transaction</v-btn>
                <v-btn small outlined :loading="syncing" :disabled="syncing" @click="syncLatest">
                  Sync Stripe
                </v-btn>
                <v-btn small outlined :loading="seeding" v-if="!productLines.length" @click="seedProductLines">
                  Seed default product lines
                </v-btn>
                <v-btn small text @click="editingLines = !editingLines">
                  {{ editingLines ? 'Done editing lines' : 'Edit product lines' }}
                </v-btn>
              </div>
            </div>

            <p class="muted small">
              Stripe = revenue source of truth. Bank = expense source of truth. Each transaction can be split across
              multiple product lines.
            </p>

            <section v-if="uncategorizedCount" class="uncat-top">
              <h3 class="section-h">
                Needs categorizing · {{ activeYear }}
                <span class="muted small">{{ uncategorizedCount }} row{{ uncategorizedCount === 1 ? '' : 's' }} — pick a product line, it drops off this list</span>
              </h3>
              <table class="ledger-table tx-table uncat-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th class="split-col">Product line</th>
                    <th class="num-col">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="tx in uncategorizedYearTransactions"
                    :key="'uncat-' + tx.id"
                    class="tx-row"
                  >
                    <td @click="openEdit(tx)">{{ tx.date }}</td>
                    <td @click="openEdit(tx)">
                      {{ tx.description || '—' }}
                      <a
                        v-if="isAmazonDescription(tx.description)"
                        class="amazon-orders-link"
                        :href="amazonOrdersUrl"
                        target="_blank"
                        rel="noopener"
                        @click.stop
                      >Orders ↗</a>
                    </td>
                    <td @click="openEdit(tx)">
                      <span class="type-pill" :class="tx.type">{{ tx.type }}</span>
                    </td>
                    <td @click="openEdit(tx)">
                      {{ tx.source }}<template v-if="bankKindLabel(tx)"> · {{ bankKindLabel(tx) }}</template>
                    </td>
                    <td class="split-col">
                      <select
                        v-for="(s, si) in tx.splits || []"
                        :key="si"
                        :value="s.productLineId"
                        class="inline-line-select"
                        :class="{ 'needs-cat': lineNameById(s.productLineId) === 'Needs categorizing' }"
                        @click.stop
                        @change="inlineChangeProductLine(tx, si, $event.target.value)"
                      >
                        <option v-for="l in productLinesForPicker" :key="l.id" :value="l.id">{{ l.name }}</option>
                      </select>
                      <select
                        v-if="!(tx.splits || []).length && tx.type !== 'transfer'"
                        value=""
                        class="inline-line-select needs-cat"
                        @click.stop
                        @change="inlineSetProductLine(tx, $event.target.value)"
                      >
                        <option value="">Uncategorized</option>
                        <option v-for="l in productLinesForPicker" :key="l.id" :value="l.id">{{ l.name }}</option>
                      </select>
                    </td>
                    <td class="num-col" :class="tx.type === 'revenue' ? 'pos' : 'neg'" @click="openEdit(tx)">
                      {{ tx.type === 'expense' ? '−' : '' }}{{ fmtMoney(tx.totalCents) }}
                    </td>
                    <td class="actions-col">
                      <button type="button" class="icon-btn" :title="'Edit'" @click.stop="openEdit(tx)">✎</button>
                      <button type="button" class="icon-btn" @click.stop="confirmDelete(tx)">×</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <details class="tools-fold">
              <summary>
                Reports, Stripe, bank &amp; rules
                <span class="muted small">Revenue, retention, sync, Capital One</span>
              </summary>

            <festival-revenue-summary :year="activeYear" />

            <retention-panel :year="activeYear" />

            <!-- Stripe sync -->
            <div class="sync-panel">
              <div class="sync-head">
                <strong>Stripe sync</strong>
                <span v-if="lastStripeSyncText" class="muted small">
                  Last imported: {{ lastStripeSyncText }}<template v-if="lastStripeSyncRangeText">
                  · covered {{ lastStripeSyncRangeText }}</template>
                </span>
                <span v-else class="muted small">Never imported.</span>
              </div>
              <div class="sync-controls">
                <v-btn
                  small
                  color="primary"
                  :loading="syncing"
                  :disabled="syncing"
                  @click="syncLatest"
                >
                  Sync latest
                </v-btn>
                <span v-if="lastStripeSyncSummary" class="muted small">
                  {{ lastStripeSyncSummary }}
                </span>
              </div>
              <div class="sync-foot muted small">
                Pulls new Stripe charges since the last sync (with a couple of days of overlap).
                Same job runs every morning at 6am Eastern with Capital One.
                Re-running is safe — charges match on Stripe id.
              </div>
              <details class="sync-advanced">
                <summary class="muted small">Custom date range</summary>
                <div class="sync-controls" style="margin-top: 8px;">
                  <label class="sync-field">
                    <span>From</span>
                    <input type="date" v-model="syncFromDate" />
                  </label>
                  <label class="sync-field">
                    <span>To</span>
                    <input type="date" v-model="syncToDate" />
                  </label>
                  <v-btn small outlined @click="setSyncRangeToFestivalYear">
                    Whole festival year
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    :loading="syncing"
                    :disabled="syncing || !canSync"
                    @click="runStripeSync"
                  >
                    Sync this range
                  </v-btn>
                </div>
              </details>
              <details v-if="syncRuns.length" class="sync-history">
                <summary>
                  Coverage — {{ syncRuns.length }} past sync run{{ syncRuns.length === 1 ? '' : 's' }}
                  <span class="muted small">(which dates have been pulled)</span>
                </summary>
                <table class="sync-history-table">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Covered</th>
                      <th class="num-col">Scanned</th>
                      <th class="num-col">Imported</th>
                      <th class="num-col">Updated</th>
                      <th class="num-col">Deleted</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(run, i) in syncRuns" :key="i">
                      <td>{{ fmtRunTime(run.at) }}</td>
                      <td>{{ fmtStamp(run.startTimestamp) }} → {{ fmtStamp(run.endTimestamp) }}</td>
                      <td class="num-col">{{ run.scanned }}</td>
                      <td class="num-col">{{ run.imported }}</td>
                      <td class="num-col">{{ run.updated }}</td>
                      <td class="num-col">{{ run.deleted }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="muted small">
                  Only runs from this point forward are logged — earlier ones weren't recorded.
                </p>
              </details>
              <div class="sync-foot muted small">
                Match rule: keep charges whose email matches a known dancer (active or canceled),
                <strong>or</strong> charges with no email at all — those are in-person Tap to Pay sales, which never
                collect one. Charges with an email that isn't a dancer's are not imported, and any previously-imported
                row that no longer matches is deleted (your manually-edited rows are kept). Exclusion rules below are
                what trim the no-email set.
              </div>
            </div>

            <!-- Known amounts -->
            <details class="known-amounts">
              <summary>
                Known charge amounts <span class="muted small">({{ knownAmountsDollars.length }} configured — also auto-pulls all store item prices)</span>
              </summary>
              <p class="muted small">
                No-email charges are all kept now, so this list no longer decides what gets imported — it just tags
                a matching charge's note with "matched known amount", which is handy for spotting store-price sales
                among the in-person ones.
              </p>
              <div class="known-row" v-for="(amt, ai) in knownAmountsDollars" :key="ai">
                <span class="known-prefix">$</span>
                <input
                  type="number"
                  step="0.01"
                  v-model.number="knownAmountsDollars[ai]"
                  class="known-input"
                  @blur="saveKnownAmounts"
                />
                <button type="button" class="icon-btn" @click="removeKnownAmount(ai)">×</button>
              </div>
              <v-btn x-small outlined class="mt-2" @click="addKnownAmount">+ Add amount</v-btn>
            </details>

            <!-- Auto-categorization rules -->
            <details class="known-amounts">
              <summary>
                Auto-categorization rules <span class="muted small">({{ categorizationRules.length }} — set the product line for matching charges)</span>
              </summary>
              <p class="muted small">
                When a transaction matches an amount AND/OR description regex, it's assigned to the named product line on import
                instead of "Needs categorizing". Amounts are comma-separated dollars (e.g. <code>291, 292, 533, 534</code>);
                blank = any. Regex is case-insensitive; blank = any. One rule list covers both sources — it runs on Stripe
                charges and Plaid bank rows during a sync, and "Apply to past" reaches both.
              </p>
              <div class="exclusion-grid">
                <div class="cat-head muted small">
                  <span>Amounts ($, comma-separated)</span>
                  <span>Description regex</span>
                  <span>Product line</span>
                  <span>Note</span>
                  <span>Past rows</span>
                  <span></span>
                </div>
                <div
                  class="cat-row"
                  :class="{ 'rule-unsaved': !categorizationRuleIsSavable(r) }"
                  v-for="(r, ri) in categorizationRules"
                  :key="ri"
                >
                  <input
                    type="text"
                    v-model="r.amountsText"
                    class="rule-input"
                    placeholder="any"
                    @blur="saveCategorizationRules"
                  />
                  <input
                    type="text"
                    v-model="r.descriptionMatch"
                    class="rule-input"
                    :placeholder="'^IABF ' + activeYear + ' Payment'"
                    @blur="saveCategorizationRules"
                  />
                  <select
                    v-model="r.productLineName"
                    class="rule-input"
                    @change="saveCategorizationRules"
                  >
                    <option value="">Choose a line…</option>
                    <!-- A rule saved against a line that has since been renamed
                         or deleted keeps its own option, so opening this editor
                         can't quietly blank it out. The sync recreates the line
                         by name on the next import. -->
                    <option
                      v-if="r.productLineName && !productLineIdByName(r.productLineName)"
                      :value="r.productLineName"
                    >{{ r.productLineName }} (missing — will be created)</option>
                    <option v-for="l in productLinesForPicker" :key="l.id" :value="l.name">{{ l.name }}</option>
                  </select>
                  <input
                    type="text"
                    v-model="r.note"
                    class="rule-input"
                    placeholder="What this catches"
                    @blur="saveCategorizationRules"
                  />
                  <button
                    type="button"
                    class="backfill-btn"
                    :disabled="applyingRule || !categorizationRuleIsSavable(r)"
                    :title="'Apply this rule to already-imported ' + activeYear + ' transactions that are still uncategorized'"
                    @click="applyCategorizationRuleHistorically(r)"
                  >
                    Apply to past
                  </button>
                  <button type="button" class="icon-btn" @click="removeCategorizationRule(ri)">×</button>
                </div>
              </div>
              <p class="muted small rule-hint">
                <strong>Apply to past</strong> re-files transactions already in the ledger for festival year
                {{ activeYear }} — only ones still sitting in "Needs categorizing" or with no product line, so it
                can't overwrite anything you've already filed. It shows you what it matched before changing anything.
              </p>
              <p class="muted small rule-hint">
                A rule needs a product line <strong>and</strong> at least one of amount / regex before it saves — an
                otherwise-empty rule would match every charge. Highlighted rows are still incomplete and aren't saved yet.
              </p>
              <v-btn x-small outlined class="mt-2" @click="addCategorizationRule">+ Add categorization rule</v-btn>
            </details>

            <!-- Exclusion rules -->
            <details class="known-amounts">
              <summary>
                Exclusion rules <span class="muted small">({{ exclusionRules.length }} — charges matching these are filtered out)</span>
              </summary>
              <p class="muted small">
                Pre-seeded with one rule for your on-demand membership ($25 invoices). Each rule can require an exact dollar amount,
                a description match (regex, case-insensitive), or both. Charges matching any rule are excluded — existing imported
                rows for them are deleted on next sync.
              </p>
              <div class="exclusion-grid">
                <div class="exclusion-head muted small">
                  <span>Amount ($, blank = any)</span>
                  <span>Description regex (blank = any)</span>
                  <span>Note</span>
                  <span>Past rows</span>
                  <span></span>
                </div>
                <div
                  class="exclusion-row"
                  :class="{ 'rule-unsaved': !exclusionRuleIsSavable(r) }"
                  v-for="(r, ri) in exclusionRules"
                  :key="ri"
                >
                  <input
                    type="number"
                    step="0.01"
                    v-model.number="r.amountDollars"
                    class="known-input"
                    placeholder="any"
                    @blur="saveExclusionRules"
                  />
                  <input
                    type="text"
                    v-model="r.descriptionMatch"
                    class="rule-input"
                    placeholder="^Invoice "
                    @blur="saveExclusionRules"
                  />
                  <input
                    type="text"
                    v-model="r.note"
                    class="rule-input"
                    placeholder="What this catches"
                    @blur="saveExclusionRules"
                  />
                  <button
                    type="button"
                    class="backfill-btn"
                    :disabled="applyingRule || !exclusionRuleIsSavable(r)"
                    :title="'Dismiss already-imported ' + activeYear + ' Stripe charges that match this rule'"
                    @click="applyExclusionRuleHistorically(r)"
                  >
                    Apply to past
                  </button>
                  <button type="button" class="icon-btn" @click="removeExclusionRule(ri)">×</button>
                </div>
              </div>
              <p class="muted small rule-hint">
                <strong>Apply to past</strong> dismisses matching Stripe charges already imported for festival year
                {{ activeYear }}, along with their fee rows — they drop out of every total, and the next sync deletes
                them for good. Reversible from the auto-dismissed list further down. Rows you've hand-edited and rows
                that didn't come from Stripe are left alone.
              </p>
              <p class="muted small rule-hint">
                A rule needs an amount or a regex before it saves. Highlighted rows are still incomplete and aren't saved yet.
              </p>
              <v-btn x-small outlined class="mt-2" @click="addExclusionRule">+ Add exclusion rule</v-btn>
            </details>

            <!-- Capital One via Plaid -->
            <div class="sync-panel">
              <div class="sync-head">
                <strong>Capital One</strong>
                <span class="muted small">
                  Checking, savings, and the IABF card — IABF-only. Stripe stays the combined BROCHE account.
                </span>
              </div>
              <p v-if="plaidError" class="error--text small">{{ plaidError }}</p>
              <p v-else-if="plaidNotice" class="muted small">{{ plaidNotice }}</p>
              <div v-if="plaidStatus && !plaidStatus.configured" class="sync-foot muted small">
                Plaid keys are not on Cloud Functions yet. Add
                <code>PLAID_CLIENT_ID</code>, <code>PLAID_SECRET</code>, and
                <code>PLAID_ENV=production</code>, then redeploy the Plaid functions.
                After that, Connect will open Capital One’s login (Plaid only keeps ~90 days of history;
                older bank rows already on the ledger stay).
              </div>
              <div v-else class="plaid-accounts">
                <div
                  v-for="item in plaidItems"
                  :key="item.itemId"
                  class="plaid-item"
                >
                  <div>
                    <strong>{{ item.institutionName }}</strong>
                    <span class="muted small">
                      <template v-if="item.lastSyncedAt"> · last sync {{ fmtRunTime(item.lastSyncedAt) }}</template>
                      <template v-if="item.lastError"> · {{ item.lastError }}</template>
                    </span>
                    <div class="muted small">
                      {{ (item.accounts || []).map(a => (a.name || a.subtype) + (a.mask ? ' ••' + a.mask : '')).join(' · ') }}
                    </div>
                  </div>
                  <div class="sync-controls">
                    <v-btn x-small outlined :loading="plaidBusy" @click="syncPlaid(item.itemId)">Sync</v-btn>
                    <v-btn x-small text :disabled="plaidBusy" @click="unlinkPlaid(item.itemId)">Disconnect</v-btn>
                  </div>
                </div>
                <p v-if="!plaidItems.length" class="muted small">No Capital One accounts connected yet.</p>
              </div>
              <div class="sync-controls">
                <v-btn
                  small
                  color="primary"
                  :loading="plaidBusy"
                  :disabled="plaidBusy || (plaidStatus && !plaidStatus.configured)"
                  @click="connectPlaid"
                >
                  {{ plaidItems.length ? 'Connect another account' : 'Connect Capital One' }}
                </v-btn>
                <v-btn
                  v-if="plaidItems.length"
                  small
                  outlined
                  :loading="plaidBusy"
                  :disabled="plaidBusy"
                  @click="syncPlaid()"
                >
                  Sync all
                </v-btn>
              </div>
              <div class="sync-foot muted small">
                Connect once. After that, Sync pulls new posted transactions into the bank ledger
                (category rules, exclusions, and transfer detection still apply). Inter-account moves and card
                payments stay transfers. A daily 6am Eastern sync runs on its own.
              </div>
            </div>

            <!-- BROCHE BALLET LLC ↔ IABF reconciliation -->
            <div class="reconcile-panel">
              <div class="reconcile-head">
                <strong>BROCHE BALLET LLC ↔ IABF reconciliation</strong>
                <span class="muted small">Did the Stripe money actually make it to the Capital One account?</span>
              </div>
              <div class="reconcile-grid">
                <div class="recon-stat">
                  <div class="recon-label">Stripe revenue (gross)</div>
                  <div class="recon-num">{{ fmtMoney(reconciliation.stripeRevenue) }}</div>
                </div>
                <div class="recon-stat">
                  <div class="recon-label">− Stripe fees</div>
                  <div class="recon-num neg">−{{ fmtMoney(reconciliation.stripeFees) }}</div>
                </div>
                <div class="recon-stat recon-stat-strong">
                  <div class="recon-label">= Net Stripe</div>
                  <div class="recon-num">{{ fmtMoney(reconciliation.netStripe) }}</div>
                </div>
                <div class="recon-stat">
                  <div class="recon-label">BROCHE → IABF transfers</div>
                  <div class="recon-num">{{ fmtMoney(reconciliation.brocheIn) }}</div>
                </div>
                <div class="recon-stat recon-stat-delta" :class="{ 'recon-ok': Math.abs(reconciliation.delta) < 100, 'recon-warn': Math.abs(reconciliation.delta) >= 100 }">
                  <div class="recon-label">Difference</div>
                  <div class="recon-num">{{ fmtMoney(reconciliation.delta) }}</div>
                </div>
              </div>
              <div class="recon-foot muted small">
                A positive Difference means net Stripe revenue hasn't yet been transferred from BROCHE BALLET LLC into the IABF
                Capital One account — money still in transit. ≈ $0 = fully reconciled. Negative means more was transferred in
                than Stripe brought in (probably an external deposit you haven't categorized yet).
              </div>
              <div v-if="staleBankRevenueCount > 0" class="recon-cleanup">
                ⚠ {{ staleBankRevenueCount }} non-Stripe revenue row{{ staleBankRevenueCount === 1 ? '' : 's' }} look{{ staleBankRevenueCount === 1 ? 's' : '' }} like
                {{ staleBankRevenueCount === 1 ? 'a transfer' : 'transfers' }} (BROCHE deposits, CC payments, inter-account moves).
                <v-btn x-small outlined :loading="reclassifyRunning" @click="reclassifyBankRevenueAsTransfers">
                  Reclassify {{ staleBankRevenueCount }} as transfer{{ staleBankRevenueCount === 1 ? '' : 's' }}
                </v-btn>
              </div>
              <details v-if="nonStripeRevenueBySource.length" class="recon-cleanup recon-diag">
                <summary>
                  <strong>Non-Stripe revenue ({{ activeYear }}): {{ fmtMoney(nonStripeRevenueTotal) }}</strong>
                  <span v-for="row in nonStripeRevenueBySource" :key="row.source" class="nsr-pill">
                    <code>{{ row.source }}</code> {{ fmtMoney(row.cents) }}
                  </span>
                  <span class="muted small" style="flex-basis: 100%;">
                    Everything in the Revenue tile that didn't come from Stripe sync. Click to expand the row list.
                  </span>
                </summary>
                <table class="ledger-table nsr-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th><code>source</code></th>
                      <th class="num-col">Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in nonStripeRevenueRows" :key="t.id">
                      <td class="nowrap">{{ t.date }}</td>
                      <td>{{ t.description || '(no description)' }}</td>
                      <td class="muted small"><code>{{ t.source || '(none)' }}</code></td>
                      <td class="num-col pos">{{ fmtMoney(t.totalCents) }}</td>
                      <td><button type="button" class="link-btn" @click="openEdit(t)">Edit →</button></td>
                    </tr>
                  </tbody>
                </table>
              </details>
            </div>

            <!-- Danger zone: full bank-feed reset (keeps Stripe revenue + fees) -->
            <details class="danger-zone">
              <summary>
                <span>⚠ Reset bank feed (keeps Stripe revenue + fees)</span>
                <span class="muted small" v-if="deletableRows.length">
                  · {{ deletableRows.length }} row{{ deletableRows.length === 1 ? '' : 's' }} would be deleted
                </span>
              </summary>
              <p class="muted small">
                Wipes <strong>everything</strong> from the bank feed (Plaid and any leftover CSV rows), plus any expense or
                transfer rows from other sources. Keeps: Stripe revenue, Stripe fees, product lines, categorization rules,
                exclusion rules. After the wipe, Plaid can re-sync from scratch. No undo.
              </p>
              <div class="danger-actions">
                <v-btn
                  small color="error"
                  :loading="wipingExpenses"
                  :disabled="!deletableRows.length"
                  @click="wipeAllExpenses"
                >
                  Delete {{ deletableRows.length }} row{{ deletableRows.length === 1 ? '' : 's' }}
                </v-btn>
                <span v-if="wipeProgress" class="muted small">{{ wipeProgress }}</span>
              </div>
            </details>

            <!-- Dismissed Stripe charges (review) -->
            <details v-if="dismissedStripe.length" class="dismissed-block">
              <summary>
                {{ dismissedStripe.length }} auto-dismissed Stripe charge{{ dismissedStripe.length === 1 ? '' : 's' }}
                — review
              </summary>
              <p class="muted small">
                These didn't match a dancer email or known amount. They stay in the DB so re-sync skips them.
                "Keep as real" un-dismisses and surfaces them in the main view.
              </p>
              <table class="ledger-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Stripe id</th>
                    <th class="num-col">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in dismissedStripe" :key="tx.id">
                    <td>{{ tx.date }}</td>
                    <td class="dismissed-desc">
                      {{ tx.description || '—' }}
                      <a
                        v-if="isAmazonDescription(tx.description)"
                        class="amazon-orders-link"
                        :href="amazonOrdersUrl"
                        target="_blank"
                        rel="noopener"
                      >Orders ↗</a>
                    </td>
                    <td class="muted small">{{ tx.sourceRef }}</td>
                    <td class="num-col">{{ fmtMoney(tx.totalCents) }}</td>
                    <td>
                      <button type="button" class="link-btn" @click="undismiss(tx)">Keep as real →</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </details>

            <details v-if="dismissedBankDuplicates.length" class="dismissed-block">
              <summary>
                {{ dismissedBankDuplicates.length }} hidden bank row{{ dismissedBankDuplicates.length === 1 ? '' : 's' }}
                — undo
              </summary>
              <p class="muted small">
                Hidden so Plaid will not re-import them. “Keep as real” puts a row back on the ledger.
              </p>
              <table class="ledger-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Description</th>
                    <th class="num-col">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in dismissedBankDuplicates" :key="tx.id">
                    <td>{{ tx.date }}</td>
                    <td>{{ bankKindLabel(tx) || tx.source }}</td>
                    <td class="dismissed-desc">
                      {{ tx.description || '—' }}
                      <a
                        v-if="isAmazonDescription(tx.description)"
                        class="amazon-orders-link"
                        :href="amazonOrdersUrl"
                        target="_blank"
                        rel="noopener"
                      >Orders ↗</a>
                    </td>
                    <td class="num-col">{{ fmtMoney(tx.totalCents) }}</td>
                    <td>
                      <button type="button" class="link-btn" @click="undismiss(tx)">Keep as real →</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </details>

            </details>

            <p v-if="loading" class="muted">Loading…</p>

            <template v-else>

              <!-- Year summary -->
              <div class="summary-row">
                <div class="summary-tile">
                  <div class="tile-label">Revenue</div>
                  <div class="tile-num pos">{{ fmtMoney(yearTotals.revenue) }}</div>
                </div>
                <div class="summary-tile">
                  <div class="tile-label">Predicted revenue</div>
                  <div class="tile-num pos">
                    <template v-if="outlookReady || activeYearEnded">{{ fmtMoney(outlookPredictedRevenueCents) }}</template>
                    <template v-else>—</template>
                  </div>
                </div>
                <div class="summary-tile">
                  <div class="tile-label">Operating expenses</div>
                  <div class="tile-num neg">{{ fmtMoney(yearTotals.operatingExpense) }}</div>
                </div>
                <div class="summary-tile">
                  <div class="tile-label">Forecast operating</div>
                  <div class="tile-num neg">{{ fmtMoney(forecastOperatingCents) }}</div>
                </div>
                <div class="summary-tile">
                  <div class="tile-label">Investments</div>
                  <div class="tile-num neg">{{ fmtMoney(yearTotals.investmentExpense) }}</div>
                  <div v-if="forecastInvestmentCents" class="tile-sub muted small">
                    forecast {{ fmtMoney(forecastInvestmentCents) }}
                  </div>
                </div>
                <div class="summary-tile summary-tile-strong">
                  <div class="tile-label">
                    {{ activeYearEnded ? 'Final profit' : 'Predicted profit' }}
                    <span v-if="activeYearEnded" class="muted small">(year closed)</span>
                  </div>
                  <div class="tile-num" :class="predictedProfit >= 0 ? 'pos' : 'neg'">
                    {{ predictedProfit < 0 ? '−' : '' }}{{ fmtMoney(Math.abs(predictedProfit)) }}
                  </div>
                </div>
              </div>

              <section class="budget-outlook">
                <h3 class="section-h">
                  Budget outlook · {{ activeYear }}
                  <span class="muted small">
                    Contracted packages − remaining unknown cancels (last year’s new/returning rates) + last year’s extras × 75%
                  </span>
                </h3>
                <p v-if="budgetLoading" class="muted small">Loading dancer packages and extras…</p>
                <template v-else>
                <table class="outlook-table">
                  <tbody>
                    <tr>
                      <td>Dancer packages</td>
                      <td class="num-col pos">{{ fmtMoney(budgetOutlook.packages.dueCents) }}</td>
                      <td class="muted small">
                        Paid {{ fmtMoney(budgetOutlook.packages.paidCents) }}
                        · outstanding {{ fmtMoney(budgetOutlook.packages.outstandingCents) }}
                        <template v-if="!budgetOutlook.packages.dueCents && yearTotals.revenue">
                          · no dancer packages loaded — using actual ledger revenue as the floor
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td>Remaining unknown cancels</td>
                      <td class="num-col neg">−{{ fmtMoney(budgetOutlook.cancels.remainingLossCents) }}</td>
                      <td class="muted small">
                        {{ cancelPhaseLabel(budgetOutlook.cancels.phase) }}
                        · still expecting {{ fmtPeople(budgetOutlook.cancels.remainingNew) }} new
                        + {{ fmtPeople(budgetOutlook.cancels.remainingRet) }} returning
                        · avg package {{ fmtMoney(budgetOutlook.cancels.overallAvgCents) }}
                        (new {{ fmtMoney(budgetOutlook.cancels.avgNewCents) }},
                        returning {{ fmtMoney(budgetOutlook.cancels.avgRetCents) }})
                        <br>
                        Last year {{ budgetOutlook.cancels.lastNewCanceled }}/{{ budgetOutlook.cancels.lastNewStarted }} new
                        ({{ fmtRate(budgetOutlook.cancels.rateNew) }})
                        · {{ budgetOutlook.cancels.lastRetCanceled }}/{{ budgetOutlook.cancels.lastRetStarted }} returning
                        ({{ fmtRate(budgetOutlook.cancels.rateRet) }})
                        · this year {{ budgetOutlook.cancels.thisNewCanceled }} new
                        + {{ budgetOutlook.cancels.thisRetCanceled }} returning already canceled
                      </td>
                    </tr>
                    <tr>
                      <td>Add-on extras (est.)</td>
                      <td class="num-col pos">{{ fmtMoney(budgetOutlook.extras.outlookCents) }}</td>
                      <td class="muted small">
                        Last year {{ fmtMoney(budgetOutlook.extras.lastYearCents) }} × 75%
                        = {{ fmtMoney(budgetOutlook.extras.estimateCents) }}
                        · booked this year {{ fmtMoney(budgetOutlook.extras.committedCents) }}
                        <template v-if="budgetOutlook.extras.inPackagesCents">
                          ({{ fmtMoney(budgetOutlook.extras.inPackagesCents) }} already in dancer packages)
                        </template>
                        · collected {{ fmtMoney(budgetOutlook.extras.thisYearCents) }}
                      </td>
                    </tr>
                    <tr class="outlook-total">
                      <td>Predicted revenue</td>
                      <td class="num-col pos">{{ fmtMoney(outlookPredictedRevenueCents) }}</td>
                      <td class="muted small">
                        <template v-if="activeYearEnded">Closed year — actual ledger revenue</template>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h4 class="full-house-h">
                  If we sell
                  <input
                    type="number"
                    min="1"
                    max="200"
                    class="full-house-n"
                    :value="fullHouseHeadcount"
                    @change="onFullHouseHeadcount($event.target.value)"
                  >
                </h4>
                <table class="outlook-table">
                  <tbody>
                    <tr>
                      <td>Package revenue</td>
                      <td class="num-col pos">{{ fmtMoney(fullHouse.packageCents) }}</td>
                      <td class="muted small">
                        {{ fullHouseHint }}
                        · {{ fullHouse.sold }} contracted
                        <template v-if="fullHouse.remaining">
                          · {{ fullHouse.remaining }} to go
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td>Remaining unknown cancels</td>
                      <td class="num-col neg">−{{ fmtMoney(fullHouseCancels.remainingLossCents) }}</td>
                      <td class="muted small">
                        Last year’s rates · extra seats counted as new (worst case)
                        · still expecting {{ fmtPeople(fullHouseCancels.remainingNew) }} new
                        + {{ fmtPeople(fullHouseCancels.remainingRet) }} returning
                      </td>
                    </tr>
                    <tr class="outlook-total">
                      <td>Projected profit</td>
                      <td
                        class="num-col"
                        :class="fullHouseProfitCents >= 0 ? 'pos' : 'neg'"
                      >
                        {{ fullHouseProfitCents < 0 ? '−' : '' }}{{ fmtMoney(Math.abs(fullHouseProfitCents)) }}
                      </td>
                      <td class="muted small">
                        packages − cancels + extras {{ fmtMoney(budgetOutlook.extras.outlookCents) }}
                        − forecast operating {{ fmtMoney(forecastOperatingCents) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <details class="outlook-extras">
                  <summary class="muted small">Last year extras breakdown ({{ activeYear - 1 }})</summary>
                  <table class="outlook-table">
                    <tbody>
                      <tr>
                        <td>Add-on services</td>
                        <td class="num-col">{{ fmtMoney(budgetOutlook.lastYearExtras.addonsCents) }}</td>
                      </tr>
                      <tr>
                        <td>Store / merch</td>
                        <td class="num-col">{{ fmtMoney(budgetOutlook.lastYearExtras.merchCents) }}</td>
                      </tr>
                      <tr>
                        <td>Guest meals / banquet</td>
                        <td class="num-col">{{ fmtMoney(budgetOutlook.lastYearExtras.guestsCents) }}</td>
                      </tr>
                      <tr>
                        <td>Costume extras</td>
                        <td class="num-col">{{ fmtMoney(budgetOutlook.lastYearExtras.costumesCents) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </details>
                </template>
              </section>

              <!-- Festival year close toggle -->
              <div class="year-close-bar">
                <v-btn small outlined :loading="endingYear" @click="toggleEndFestivalYear">
                  {{ activeYearEnded ? 'Reopen festival ' + activeYear : 'End festival ' + activeYear }}
                </v-btn>
                <span class="muted small" v-if="activeYearEnded">
                  Closed — Final profit is actual revenue minus operating expense. Investments (venue assets,
                  floor, barre) are shown separately and do not hit profit.
                </span>
                <span class="muted small" v-else>
                  Open — Predicted profit is outlook revenue minus operating lines at max(forecast, actual).
                  Investments (venue assets, floor, barre) stay out of profit.
                </span>
              </div>

              <!-- By product line -->
              <h3 class="section-h">By product line · {{ activeYear }}</h3>
              <table class="ledger-table">
                <thead>
                  <tr>
                    <th class="line-col">Product line</th>
                    <th class="num-col" title="What you planned this line would bring in">Fc revenue</th>
                    <th class="num-col">Revenue</th>
                    <th class="num-col" title="What you planned this line would cost">Fc expense</th>
                    <th class="num-col">Expense</th>
                    <th class="num-col">Net</th>
                    <th class="num-col" title="Actual net minus planned net. Blank until this line has a transaction — a profitable plan is not a miss.">vs forecast</th>
                    <th class="line-note">Note</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in groupedLineRows" :key="item.id"
                    :class="{
                      'line-section-row': item.type === 'section',
                      'line-total-row': item.type === 'subtotal',
                      'line-uncat': item.row && item.row.isUncategorized,
                      'line-over': item.row && item.row.hasForecast && item.row.hasActuals && item.row.variance < 0,
                      'line-under': item.row && item.row.hasForecast && item.row.hasActuals && item.row.variance > 0
                    }"
                  >
                    <template v-if="item.type === 'section'">
                      <td colspan="8" class="line-section">{{ item.label }}</td>
                    </template>
                    <template v-else-if="item.type === 'subtotal'">
                      <td>{{ item.label }}</td>
                      <td class="num-col">{{ item.totals.forecastRevenue ? fmtMoney(item.totals.forecastRevenue) : '—' }}</td>
                      <td class="num-col pos">{{ item.totals.revenue ? fmtMoney(item.totals.revenue) : '—' }}</td>
                      <td class="num-col">{{ item.totals.forecastExpense ? fmtMoney(item.totals.forecastExpense) : '—' }}</td>
                      <td class="num-col neg">{{ item.totals.expense ? fmtMoney(item.totals.expense) : '—' }}</td>
                      <td class="num-col" :class="item.totals.net > 0 ? 'pos' : (item.totals.net < 0 ? 'neg' : '')">
                        <template v-if="!item.totals.revenue && !item.totals.expense">—</template>
                        <template v-else>{{ item.totals.net < 0 ? '−' : '' }}{{ fmtMoney(Math.abs(item.totals.net)) }}</template>
                      </td>
                      <td class="num-col"></td>
                      <td class="line-note"></td>
                    </template>
                    <template v-else>
                      <td>
                        <button
                          type="button"
                          class="line-name-btn"
                          :class="{ 'is-filtered': lineFilterId === item.row.line.id }"
                          :title="'Show the ' + activeYear + ' transactions making up this line'"
                          @click="filterByLine(item.row.line.id)"
                        >{{ item.row.line.name }}</button>
                      </td>
                      <td class="num-col">
                        <div v-if="isForecastCellOpen(item.row, 'rev')" class="forecast-cell">
                          <input
                            :ref="'fc-rev-' + item.row.line.id"
                            type="number"
                            step="0.01"
                            class="forecast-input"
                            :class="{ 'forecast-inherited': item.row.forecastRevenueSource === 'prior-actual' }"
                            placeholder="—"
                            :title="forecastInputTitle(item.row, 'rev')"
                            :value="forecastInputValue(item.row, 'rev')"
                            @input="onForecastDraftInput(item.row, 'rev', $event.target.value)"
                            @blur="onForecastBlur(item.row, 'rev')"
                          />
                          <button
                            v-if="item.row.forecastRevenueSource === 'typed'"
                            type="button"
                            class="forecast-reset"
                            title="Clear this year’s plan — back to last year’s actuals if we have them"
                            @mousedown.prevent
                            @click="resetForecast(item.row, 'rev')"
                          >
                            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                              <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M3.2 8a4.8 4.8 0 1 0 1.4-3.4M3.2 3.2v3h3" />
                            </svg>
                          </button>
                        </div>
                        <button
                          v-else-if="!item.row.isUncategorized"
                          type="button"
                          class="forecast-add"
                          title="Forecast revenue for this line"
                          @click="revealForecastCell(item.row, 'rev')"
                        >—</button>
                        <span v-else>—</span>
                      </td>
                      <td class="num-col pos">{{ item.row.revenue ? fmtMoney(item.row.revenue) : '—' }}</td>
                      <td class="num-col">
                        <div v-if="isForecastCellOpen(item.row, 'exp')" class="forecast-cell">
                          <input
                            :ref="'fc-exp-' + item.row.line.id"
                            type="number"
                            step="0.01"
                            class="forecast-input"
                            :class="{ 'forecast-inherited': item.row.forecastExpenseSource === 'prior-actual' }"
                            placeholder="—"
                            :title="forecastInputTitle(item.row, 'exp')"
                            :value="forecastInputValue(item.row, 'exp')"
                            @input="onForecastDraftInput(item.row, 'exp', $event.target.value)"
                            @blur="onForecastBlur(item.row, 'exp')"
                          />
                          <button
                            v-if="item.row.forecastExpenseSource === 'typed'"
                            type="button"
                            class="forecast-reset"
                            title="Clear this year’s plan — back to last year’s actuals if we have them"
                            @mousedown.prevent
                            @click="resetForecast(item.row, 'exp')"
                          >
                            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                              <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M3.2 8a4.8 4.8 0 1 0 1.4-3.4M3.2 3.2v3h3" />
                            </svg>
                          </button>
                        </div>
                        <button
                          v-else-if="!item.row.isUncategorized"
                          type="button"
                          class="forecast-add"
                          title="Forecast expense for this line"
                          @click="revealForecastCell(item.row, 'exp')"
                        >—</button>
                        <span v-else>—</span>
                      </td>
                      <td class="num-col neg">{{ item.row.expense ? fmtMoney(item.row.expense) : '—' }}</td>
                      <td class="num-col" :class="item.row.net > 0 ? 'pos' : (item.row.net < 0 ? 'neg' : '')">
                        <template v-if="!item.row.revenue && !item.row.expense">—</template>
                        <template v-else>{{ item.row.net < 0 ? '−' : '' }}{{ fmtMoney(Math.abs(item.row.net)) }}</template>
                      </td>
                      <td
                        class="num-col"
                        :class="item.row.variance > 0 ? 'pos' : (item.row.variance < 0 ? 'neg' : '')"
                        :title="vsForecastTitle(item.row)"
                      >
                        <template v-if="!item.row.hasForecast || !item.row.hasActuals">—</template>
                        <template v-else>{{ item.row.variance > 0 ? '+' : (item.row.variance < 0 ? '−' : '') }}{{ fmtMoney(Math.abs(item.row.variance)) }}</template>
                      </td>
                      <td class="line-note muted small">{{ item.row.line.notes || '' }}</td>
                    </template>
                  </tr>
                  <tr v-if="!lineRows.length">
                    <td colspan="8" class="muted small">No transactions yet for {{ activeYear }}.</td>
                  </tr>
                </tbody>
              </table>

              <!-- Edit product lines (inline) -->
              <div v-if="editingLines" class="lines-editor">
                <h3 class="section-h">Product lines</h3>
                <div class="line-edit line-edit-head muted small">
                  <span>Name</span>
                  <span>Order</span>
                  <span>Kind</span>
                  <span>Note</span>
                  <span></span>
                </div>
                <p class="muted small">
                  Forecast revenue and expense are typed straight into the table above, next to what each line
                  actually did. Investment lines (venue assets, floor, barre) sit outside operating profit.
                </p>
                <div v-for="(line, li) in productLinesSorted" :key="line.id || ('new' + li)" class="line-edit">
                  <input v-model="line.name" class="line-input" placeholder="Line name" @blur="onLineFieldBlur(line)" />
                  <input v-model.number="line.sortOrder" type="number" class="line-order" @blur="onLineFieldBlur(line)" />
                  <select
                    class="line-kind"
                    :value="lineKind(line)"
                    @change="setLineKind(line, $event.target.value)"
                  >
                    <option value="operating">Operating</option>
                    <option value="investment">Investment</option>
                  </select>
                  <input v-model="line.notes" class="line-notes" placeholder="Optional note" @blur="onLineFieldBlur(line)" />
                  <button type="button" class="icon-btn" @click="confirmDeleteLine(line)">×</button>
                </div>
                <v-btn x-small outlined class="mt-2" @click="addLineRow">+ Add product line</v-btn>
              </div>

              <!-- Transactions -->
              <h3 class="section-h" ref="txSection">Transactions · {{ activeYear }}</h3>
              <div v-if="lineFilterSummary" class="line-filter-chip">
                <strong>{{ lineFilterSummary.name }}</strong>
                — {{ lineFilterSummary.rows }} {{ lineFilterSummary.rows === 1 ? 'row' : 'rows' }},
                {{ fmtMoney(lineFilterSummary.cents) }} on this line
                <button type="button" class="chip-clear" title="Clear" @click="lineFilterId = ''">×</button>
              </div>
              <div class="tx-filters">
                <input
                  type="search"
                  v-model="txSearch"
                  class="tx-search"
                  placeholder="Search description, email, note or Stripe id…"
                />
                <label class="tx-filter">
                  <input type="checkbox" v-model="uncategorizedOnly" />
                  <span>Only uncategorized</span>
                </label>
                <!-- Deliberately not persisted: it resets on reload so it can't
                     stay armed by accident. -->
                <label class="tx-filter tx-filter-armed" :class="{ 'is-armed': skipDeleteConfirm }">
                  <input type="checkbox" v-model="skipDeleteConfirm" />
                  <span v-if="skipDeleteConfirm">⚠ Deleting without asking — resets on reload</span>
                  <span v-else>Skip delete confirmation</span>
                </label>
                <span class="muted small">
                  {{ uncategorizedCount }} of {{ yearTransactions.length }}
                  {{ yearTransactions.length === 1 ? 'row' : 'rows' }} still need a product line
                </span>
                <span v-if="txSearch.trim()" class="muted small">
                  · {{ visibleTransactions.length }} match{{ visibleTransactions.length === 1 ? '' : 'es' }} in {{ activeYear }}
                </span>
                <!-- The list only ever renders one festival year, which is the
                     usual reason a search "finds nothing" — say where the rest are. -->
                <span v-if="txSearch.trim() && searchHitsOtherYears.length" class="muted small search-elsewhere">
                  Also
                  <button
                    v-for="hit in searchHitsOtherYears"
                    :key="hit.year"
                    type="button"
                    class="link-btn"
                    @click="activeYear = hit.year"
                  >{{ hit.count }} in {{ hit.year }}</button>
                </span>
                <span v-if="txSearch.trim() && searchHitsDismissed" class="muted small">
                  · {{ searchHitsDismissed }} dismissed (see the auto-dismissed list below)
                </span>
              </div>
              <table class="ledger-table tx-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th class="split-col">Product line</th>
                    <th class="num-col">Amount</th>
                    <th class="num-col">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="tx in visibleTransactions"
                    :key="tx.id"
                    class="tx-row"
                  >
                    <td @click="openEdit(tx)">{{ tx.date }}</td>
                    <td @click="openEdit(tx)">
                      {{ tx.description || '—' }}
                      <a
                        v-if="isAmazonDescription(tx.description)"
                        class="amazon-orders-link"
                        :href="amazonOrdersUrl"
                        target="_blank"
                        rel="noopener"
                        @click.stop
                      >Orders ↗</a>
                    </td>
                    <td @click="openEdit(tx)">
                      <span class="type-pill" :class="tx.type">{{ tx.type }}</span>
                    </td>
                    <td @click="openEdit(tx)">
                      {{ tx.source }}<template v-if="bankKindLabel(tx)"> · {{ bankKindLabel(tx) }}</template>
                    </td>
                    <td class="split-col">
                      <select
                        v-for="(s, si) in tx.splits || []"
                        :key="si"
                        :value="s.productLineId"
                        class="inline-line-select"
                        :class="{ 'needs-cat': lineNameById(s.productLineId) === 'Needs categorizing' }"
                        @click.stop
                        @change="inlineChangeProductLine(tx, si, $event.target.value)"
                      >
                        <option v-for="l in productLinesForPicker" :key="l.id" :value="l.id">{{ l.name }}</option>
                      </select>
                      <!-- Rows that arrived with no splits at all — bank imports,
                           mostly — still need somewhere to pick a line. Transfers
                           are left out: they carry no product line by design. -->
                      <select
                        v-if="!(tx.splits || []).length && tx.type !== 'transfer'"
                        value=""
                        class="inline-line-select needs-cat"
                        @click.stop
                        @change="inlineSetProductLine(tx, $event.target.value)"
                      >
                        <option value="">Uncategorized</option>
                        <option v-for="l in productLinesForPicker" :key="l.id" :value="l.id">{{ l.name }}</option>
                      </select>
                    </td>
                    <td class="num-col split-amounts" @click="openEdit(tx)">
                      <div v-for="(s, si) in tx.splits || []" :key="si">
                        {{ fmtMoney(s.amountCents) }}
                      </div>
                    </td>
                    <td class="num-col" :class="tx.type === 'revenue' ? 'pos' : 'neg'" @click="openEdit(tx)">
                      {{ tx.type === 'expense' ? '−' : '' }}{{ fmtMoney(tx.totalCents) }}
                      <div v-if="feeForTx(tx)" class="fee-inline">
                        − {{ fmtMoney(feeForTx(tx).totalCents) }} fee
                      </div>
                    </td>
                    <td class="actions-col">
                      <a
                        v-if="stripeUrlFor(tx)"
                        :href="stripeUrlFor(tx)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="icon-btn stripe-link"
                        title="Open this charge in Stripe (new tab)"
                        @click.stop
                      >↗</a>
                      <button type="button" class="icon-btn" :title="'Edit'" @click.stop="openEdit(tx)">✎</button>
                      <button type="button" class="icon-btn" @click.stop="confirmDelete(tx)">×</button>
                    </td>
                  </tr>
                  <tr v-if="!visibleTransactions.length">
                    <td colspan="8" class="muted small">
                      <template v-if="txSearch.trim()">
                        No {{ activeYear }} row matches "{{ txSearch.trim() }}"<template
                          v-if="uncategorizedOnly"> among uncategorized rows</template>.
                        <template v-if="searchHitsOtherYears.length">
                          There {{ searchHitsOtherYears.length === 1 ? 'is' : 'are' }} a match in
                          <button
                            v-for="hit in searchHitsOtherYears"
                            :key="hit.year"
                            type="button"
                            class="link-btn"
                            @click="activeYear = hit.year"
                          >{{ hit.year }}</button>
                          though — the list only shows one festival year at a time.
                        </template>
                      </template>
                      <template v-else-if="lineFilterId">
                        Nothing in {{ activeYear }} is filed under {{ lineFilterSummary.name }}<template
                          v-if="uncategorizedOnly"> that's still uncategorized</template>.
                      </template>
                      <template v-else-if="uncategorizedOnly && yearTransactions.length">
                        Nothing left to categorize in {{ activeYear }}. Untick "Only uncategorized" to see all
                        {{ yearTransactions.length }} rows.
                      </template>
                      <template v-else>No transactions yet for {{ activeYear }}.</template>
                    </td>
                  </tr>
                </tbody>
              </table>

            </template>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>

    <!-- Transaction dialog -->
    <div v-if="dialogOpen" class="dialog-overlay" @click.self="closeDialog">
      <div class="dialog-card">
        <h3>{{ editingTx.id ? 'Edit transaction' : 'New transaction' }}</h3>

        <div class="form-grid">
          <label class="form-field">
            <span>Date</span>
            <input type="date" v-model="editingTx.date" />
          </label>
          <label class="form-field">
            <span>Festival year <span class="muted small">(auto: {{ festivalYearFromDate(editingTx.date) }})</span></span>
            <input type="number" v-model.number="editingTx.festivalYear" :placeholder="String(festivalYearFromDate(editingTx.date))" />
          </label>
          <label class="form-field">
            <span>Type</span>
            <select v-model="editingTx.type">
              <option value="revenue">Revenue</option>
              <option value="expense">Expense</option>
              <option value="transfer">Transfer (moving your own money)</option>
            </select>
          </label>
          <p v-if="editingTx.type === 'transfer'" class="muted small form-note">
            Transfers are left out of revenue, expense and profit — they're money moving between your own
            accounts, not a gain or a cost. They carry no product line.
          </p>
          <label class="form-field">
            <span>Source</span>
            <select v-model="editingTx.source">
              <option value="manual">Manual</option>
              <option value="stripe">Stripe</option>
              <option value="bank">Bank</option>
            </select>
          </label>
          <label class="form-field full">
            <span>Source ref</span>
            <input v-model="editingTx.sourceRef" placeholder="e.g. ch_… or bank row id" />
          </label>
        </div>

        <label class="form-field full">
          <span>Description</span>
          <input v-model="editingTx.description" placeholder="What was this transaction?" />
          <a
            v-if="editingTx && isAmazonDescription(editingTx.description)"
            class="amazon-orders-link amazon-orders-link--block"
            :href="amazonOrdersUrl"
            target="_blank"
            rel="noopener"
          >Open Amazon orders ↗</a>
        </label>

        <div class="splits-block">
          <div class="splits-header">
            <strong>Splits</strong>
            <span class="muted small">Each split assigns part of this transaction to one product line.</span>
          </div>
          <div v-for="(s, si) in editingTx.splits" :key="si" class="split-row">
            <select v-model="s.productLineId" class="split-line">
              <option value="">—</option>
              <option v-for="l in productLinesForPicker" :key="l.id" :value="l.id">{{ l.name }}</option>
            </select>
            <input
              type="number"
              step="0.01"
              v-model.number="s.amountDollars"
              placeholder="0.00"
              class="split-amount"
              @input="onSplitAmountInput(s)"
            />
            <input v-model="s.note" placeholder="optional note" class="split-note" />
            <button type="button" class="icon-btn" :disabled="editingTx.splits.length <= 1" @click="removeSplit(si)">×</button>
          </div>
          <v-btn x-small outlined @click="addSplit">+ Add split</v-btn>

          <div class="split-total">
            Total: <strong>{{ fmtMoney(splitSumCents) }}</strong>
            <span v-if="editingTx.type === 'expense'" class="muted small">(expense)</span>
          </div>
        </div>

        <label class="form-field full">
          <span>Notes</span>
          <textarea v-model="editingTx.notes" rows="2" placeholder="Anything else worth remembering"></textarea>
        </label>

        <div v-if="dialogError" class="error-text">{{ dialogError }}</div>

        <div class="dialog-actions">
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn v-if="editingTx.id" text color="error" :loading="deleting" @click="onDelete">Delete</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="onSave">Save</v-btn>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { IABF, iabfForYear, dancersCollection } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import FestivalRevenueSummary from '@/components/iabf/FestivalRevenueSummary.vue'
import RetentionPanel from '@/components/iabf/RetentionPanel.vue'
import { buildBudgetOutlook, predictedRevenueCents, resolveLineForecastExpense, resolveLineForecastRevenue, fullHouseRevenue, parseForecastInput, scaleCancelLoss, DEFAULT_FULL_HOUSE_HEADCOUNT } from '@/utils/iabf-budget-outlook'

const DEFAULT_LINES = [
  'Classes',
  'Faculty',
  'Competition medals & prizes',
  'Rooms (onsite)',
  'Merch',
  'Costume',
  'Carpools',
  'Photoshoots',
  'Final banquet',
  'Welcome gifts',
  'Photo & video of event',
  'Credit card / Stripe fees',
  'Refunds & held deposits',
  'Meal plans for guests',
  'Other income (interest, CC rewards)',
  'Venue Assets (e.g. hairdryers, umbrellas, speakers)',
  'Capital investments (floor, barre, etc.)',
  'Misc'
]

function emptyTx (festivalYear) {
  // Use today's calendar date; the festival year is supplied separately so
  // a manual entry for an earlier festival is easy to log mid-cycle.
  const d = new Date()
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return {
    date: dateStr,
    festivalYear: festivalYear || null,
    type: 'revenue',
    source: 'manual',
    sourceRef: '',
    description: '',
    splits: [emptySplit()],
    notes: ''
  }
}

function emptySplit () {
  return { productLineId: '', amountDollars: null, amountCents: 0, note: '' }
}

const VENUE_ASSETS_NAME = 'Venue Assets (e.g. hairdryers, umbrellas, speakers)'

const AMAZON_ORDERS_URL = 'https://www.amazon.com/gp/css/order-history'

function isAmazonDescription (description) {
  return /amazon|\bamzn\b/i.test(String(description || ''))
}

function nameLooksLikeInvestment (name) {
  return /capital investment|venue assets|venue supplies/i.test(String(name || ''))
}

function sumLineRows (rows) {
  const t = (rows || []).reduce((acc, r) => ({
    forecastRevenue: acc.forecastRevenue + (r.forecastRevenue || 0),
    revenue: acc.revenue + (r.revenue || 0),
    forecastExpense: acc.forecastExpense + (r.forecastExpense || 0),
    expense: acc.expense + (r.expense || 0),
    net: acc.net + (r.net || 0),
    eacRevenue: acc.eacRevenue + (r.eacRevenue || 0),
    eacExpense: acc.eacExpense + (r.eacExpense || 0)
  }), { forecastRevenue: 0, revenue: 0, forecastExpense: 0, expense: 0, net: 0, eacRevenue: 0, eacExpense: 0 })
  t.forecastNet = t.forecastRevenue - t.forecastExpense
  t.variance = t.net - t.forecastNet
  return t
}

export default {
  components: { FestivalRevenueSummary, RetentionPanel },
  data () {
    return {
      activeYear: new Date().getFullYear(),
      amazonOrdersUrl: AMAZON_ORDERS_URL,
      loading: true,
      seeding: false,
      editingLines: false,
      unsubLines: null,
      unsubTxs: null,
      dialogOpen: false,
      editingTx: emptyTx(new Date().getFullYear()),
      saving: false,
      deleting: false,
      dialogError: '',
      syncFromDate: '',
      syncToDate: '',
      syncing: false,
      syncMeta: null,
      unsubSyncMeta: null,
      knownAmountsDoc: null,
      unsubKnownAmounts: null,
      knownAmountsDollars: [],
      exclusionRules: [],
      exclusionRulesDoc: null,
      unsubExclusionRules: null,
      categorizationRules: [],
      categorizationRulesDoc: null,
      unsubCategorizationRules: null,
      // These three editors hydrate from Firestore exactly once. Their save
      // step drops incomplete rows before writing (a rule with no product
      // line, or no amount + no regex, would match every charge), so a
      // snapshot echo of our own write would delete the half-typed row out
      // from under the cursor. This page is the only writer of those docs.
      knownAmountsHydrated: false,
      exclusionRulesHydrated: false,
      categorizationRulesHydrated: false,
      applyingRule: false,
      uncategorizedOnly: false,
      txSearch: '',
      skipDeleteConfirm: false,
      lineFilterId: '',
      revealedForecastCells: [],
      // Per-festival-year metadata (currently just "ended" flag, which
      // switches per-line forecasts from max(budget, actual) to actual).
      yearMetaByYear: {},
      unsubYearMeta: null,
      endingYear: false,
      reclassifyRunning: false,
      wipingExpenses: false,
      wipeProgress: '',
      plaidStatus: null,
      plaidBusy: false,
      plaidError: '',
      plaidNotice: '',
      budgetThisYear: { dancers: [], addonServices: [], storeOrders: [], guests: [], canceled: [] },
      budgetLastYear: { dancers: [], addonServices: [], storeOrders: [], guests: [], canceled: [] },
      budgetPeople: [],
      budgetLoading: false,
      budgetLoadedForYear: null,
      budgetLoadSeq: 0,
      budgetRoomLimits: {},
      fullHouseHeadcount: DEFAULT_FULL_HOUSE_HEADCOUNT,
      forecastDrafts: {}
    }
  },
  computed: {
    productLines () {
      return this.$store.getters.accountingProductLines || []
    },
    productLinesSorted () {
      return this.productLines
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || (a.name || '').localeCompare(b.name || ''))
    },
    // The pickers are alphabetical rather than in report order: with 20+
    // lines you find one by name, and a browser's built-in type-to-jump on
    // a <select> only works if the options are in name order. The summary
    // table and the line editor keep sortOrder — that's reading order.
    productLinesForPicker () {
      return this.productLines
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    transactions () {
      return this.$store.getters.accountingTransactions || []
    },
    feeByParentRef () {
      // Maps a parent charge's sourceRef → its fee expense doc, so we can
      // show the fee inline on the parent row instead of as its own row.
      const map = new Map()
      for (const t of this.transactions) {
        const ref = String(t.sourceRef || '')
        if (t.source === 'stripe' && ref.endsWith(':fee')) {
          map.set(ref.slice(0, -':fee'.length), t)
        }
      }
      return map
    },
    yearLedger () {
      // Everything booked to the active festival year, Stripe fee rows
      // included. All the totals run off this. The visible transaction
      // list runs off yearTransactions, which hides the fee rows.
      return this.transactions.filter(t => this.txFestivalYear(t) === this.activeYear && !t.dismissed)
    },
    yearTransactions () {
      // Hide the standalone Stripe-fee expense rows from the list — they're
      // surfaced inline on their parent charge instead. Totals iterate
      // yearLedger, so the fees still count toward expense and toward the
      // "Credit card / Stripe fees" product line.
      return this.yearLedger
        .filter(t => !(t.source === 'stripe' && String(t.sourceRef || '').endsWith(':fee')))
        .slice()
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    },
    // Same definition the "apply to past" buttons use: no splits, a blank
    // product line, or parked in "Needs categorizing".
    uncategorizedYearTransactions () {
      return this.yearTransactions.filter(t => t.type !== 'transfer' && this.txIsUncategorized(t))
    },
    uncategorizedCount () {
      return this.uncategorizedYearTransactions.length
    },
    dismissedBankDuplicates () {
      return this.transactions
        .filter(t => t.dismissed && t.source === 'bank' && t.dismissedReason === 'csv-plaid-duplicate')
        .slice()
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    },
    visibleTransactions () {
      const lineFilter = this.txMatchesLineFilter
      // A line filter runs over the whole ledger rather than the display list:
      // filtering by "Credit card / Stripe fees" has to reach the fee rows,
      // which the list otherwise hides behind their parent charge.
      let rows = lineFilter
        ? this.yearLedger.slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        : (this.uncategorizedOnly ? this.uncategorizedYearTransactions : this.yearTransactions)
      if (lineFilter) {
        if (this.uncategorizedOnly) {
          rows = rows.filter(t => t.type !== 'transfer' && this.txIsUncategorized(t))
        }
        rows = rows.filter(lineFilter)
      }
      return this.txMatchesSearch ? rows.filter(this.txMatchesSearch) : rows
    },
    // null when no line is selected, so callers can skip filtering entirely.
    txMatchesLineFilter () {
      const id = this.lineFilterId
      if (!id) return null
      if (id === '__uncat__') return tx => !(tx.splits || []).length
      return tx => (tx.splits || []).some(s => s.productLineId === id)
    },
    lineFilterSummary () {
      if (!this.lineFilterId) return null
      const isUncat = this.lineFilterId === '__uncat__'
      let cents = 0
      for (const tx of this.visibleTransactions) {
        if (isUncat) {
          cents += Number(tx.totalCents) || 0
          continue
        }
        for (const s of (tx.splits || [])) {
          if (s.productLineId === this.lineFilterId) cents += Number(s.amountCents) || 0
        }
      }
      return {
        name: isUncat ? 'Uncategorized' : this.lineNameById(this.lineFilterId),
        rows: this.visibleTransactions.length,
        cents
      }
    },
    // null when the box is empty, so callers can skip filtering entirely.
    txMatchesSearch () {
      const needle = this.txSearch.trim().toLowerCase()
      if (!needle) return null
      return tx => [tx.description, tx.notes, tx.sourceRef, tx.source, tx.date]
        .some(field => String(field || '').toLowerCase().includes(needle))
    },
    // Matches sitting in a festival year the list isn't showing.
    searchHitsOtherYears () {
      if (!this.txMatchesSearch) return []
      const counts = new Map()
      for (const t of this.transactions) {
        if (t.dismissed) continue
        if (t.source === 'stripe' && String(t.sourceRef || '').endsWith(':fee')) continue
        const fy = this.txFestivalYear(t)
        if (fy === this.activeYear) continue
        if (!this.txMatchesSearch(t)) continue
        counts.set(fy, (counts.get(fy) || 0) + 1)
      }
      return Array.from(counts.entries())
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => b.year - a.year)
    },
    searchHitsDismissed () {
      if (!this.txMatchesSearch) return 0
      return this.transactions.filter(t => t.dismissed && this.txMatchesSearch(t)).length
    },
    yearOptions () {
      const set = new Set([2024, 2025, 2026, 2027])
      for (const t of this.transactions) {
        const fy = this.txFestivalYear(t)
        if (fy) set.add(fy)
      }
      return Array.from(set).sort((a, b) => b - a)
    },
    yearTotals () {
      let revenue = 0
      let operatingExpense = 0
      let investmentExpense = 0
      const investmentIds = this.investmentLineIds
      for (const t of this.yearLedger) {
        const total = Number(t.totalCents) || 0
        if (t.type === 'revenue') {
          revenue += total
          continue
        }
        if (t.type !== 'expense') {
          continue
        }
        const splits = t.splits || []
        if (!splits.length) {
          operatingExpense += total
          continue
        }
        let splitSum = 0
        for (const s of splits) {
          const amt = Math.round(Number(s.amountCents) || 0)
          splitSum += amt
          if (investmentIds.has(s.productLineId)) {
            investmentExpense += amt
          } else {
            operatingExpense += amt
          }
        }
        if (total > splitSum) {
          operatingExpense += (total - splitSum)
        }
      }
      const expense = operatingExpense + investmentExpense
      return {
        revenue,
        expense,
        operatingExpense,
        investmentExpense,
        net: revenue - operatingExpense
      }
    },
    investmentLineIds () {
      const ids = new Set()
      for (const line of this.productLines) {
        if (this.lineIsInvestment(line) && line.id) {
          ids.add(line.id)
        }
      }
      return ids
    },
    lineTotalsById () {
      return this.lineTotalsByIdForYear(this.activeYear)
    },
    priorYearLineTotalsById () {
      return this.lineTotalsByIdForYear(this.activeYear - 1)
    },
    activeYearEnded () {
      const m = this.yearMetaByYear || {}
      const y = m[String(this.activeYear)] || {}
      return !!y.ended
    },
    activeYearEndedAt () {
      const m = this.yearMetaByYear || {}
      const y = m[String(this.activeYear)] || {}
      return y.endedAt || null
    },
    lineRows () {
      const ended = this.activeYearEnded
      // EAC pattern: while the festival is in progress, forecast each
      // line as max(budget, actual). Once the year is closed, switch
      // to actual — under-budget savings are now real.
      const named = this.productLinesSorted
        .map(line => {
          const t = this.lineTotalsById[line.id] || { revenue: 0, expense: 0 }
          const revPlan = this.lineForecastRevenueResolved(line)
          const expPlan = this.lineForecastExpenseResolved(line)
          const forecastRevenue = revPlan.cents
          const forecastExpense = expPlan.cents
          const net = t.revenue - t.expense
          const forecastNet = forecastRevenue - forecastExpense
          return {
            line,
            isInvestment: this.lineIsInvestment(line),
            revenue: t.revenue,
            expense: t.expense,
            net,
            forecastRevenue,
            forecastExpense,
            forecastRevenueSource: revPlan.source,
            forecastExpenseSource: expPlan.source,
            forecastNet,
            hasForecast: !!(forecastRevenue || forecastExpense),
            hasActuals: !!(t.revenue || t.expense),
            // Actual net minus planned net. Positive = beat the plan.
            // Hidden until something posts — $0 actual vs a revenue plan
            // would look like a miss.
            variance: net - forecastNet,
            // Estimate at completion, for the profit tile only: mid-festival
            // a line can't finish below what it has already spent or earned.
            eacRevenue: ended ? t.revenue : Math.max(forecastRevenue, t.revenue),
            eacExpense: ended ? t.expense : Math.max(forecastExpense, t.expense)
          }
        })
        .filter(r => r.revenue || r.expense || r.hasForecast || r.isInvestment)
      // Transactions with no splits (uncategorized) don't land in any
      // per-line bucket — surface them as a virtual "Uncategorized" row
      // so the table totals match the summary tiles. User triages later.
      let uncatRev = 0
      let uncatExp = 0
      for (const t of this.yearLedger) {
        if (!t.splits || t.splits.length === 0) {
          const total = Number(t.totalCents) || 0
          if (t.type === 'revenue') uncatRev += total
          else if (t.type === 'expense') uncatExp += total
        }
      }
      if (uncatRev || uncatExp) {
        named.push({
          line: { id: '__uncat__', name: 'Uncategorized (triage later)' },
          isInvestment: false,
          revenue: uncatRev,
          expense: uncatExp,
          net: uncatRev - uncatExp,
          forecastRevenue: 0,
          forecastExpense: 0,
          forecastRevenueSource: 'none',
          forecastExpenseSource: 'none',
          forecastNet: 0,
          hasForecast: false,
          hasActuals: !!(uncatRev || uncatExp),
          variance: 0,
          eacRevenue: uncatRev,
          eacExpense: uncatExp,
          isUncategorized: true
        })
      }
      return named
    },
    lineRowTotals () {
      return sumLineRows(this.lineRows)
    },
    operatingLineRows () {
      return this.lineRows.filter(r => !r.isInvestment)
    },
    investmentLineRows () {
      return this.lineRows.filter(r => r.isInvestment)
    },
    operatingTotals () {
      return sumLineRows(this.operatingLineRows)
    },
    investmentTotals () {
      return sumLineRows(this.investmentLineRows)
    },
    groupedLineRows () {
      const out = []
      if (this.operatingLineRows.length) {
        out.push({ type: 'section', id: 'sec-op', label: 'Operating' })
        for (const row of this.operatingLineRows) {
          out.push({ type: 'row', id: 'row-' + row.line.id, row })
        }
        out.push({ type: 'subtotal', id: 'tot-op', label: 'Operating total', totals: this.operatingTotals })
      }
      if (this.investmentLineRows.length) {
        out.push({
          type: 'section',
          id: 'sec-inv',
          label: 'Investments — not in operating profit'
        })
        for (const row of this.investmentLineRows) {
          out.push({ type: 'row', id: 'row-' + row.line.id, row })
        }
        out.push({ type: 'subtotal', id: 'tot-inv', label: 'Investments total', totals: this.investmentTotals })
      }
      return out
    },
    forecastOperatingCents () {
      return this.operatingTotals.eacExpense
    },
    forecastInvestmentCents () {
      return this.investmentTotals.eacExpense
    },
    forecastTotalCents () {
      return this.forecastOperatingCents
    },
    outlookReady () {
      return this.budgetLoadedForYear === this.activeYear
    },
    budgetOutlook () {
      return buildBudgetOutlook({
        festivalYear: this.activeYear,
        now: new Date(),
        yearEnded: this.activeYearEnded,
        people: this.budgetPeople,
        dancers: this.budgetThisYear.dancers,
        thisYearCanceled: this.budgetThisYear.canceled,
        lastYearDancers: this.budgetLastYear.dancers,
        lastYearCanceled: this.budgetLastYear.canceled,
        lastYear: this.budgetLastYear,
        thisYear: this.budgetThisYear,
        actualRevenueCents: this.yearTotals.revenue
      })
    },
    fullHouse () {
      const limits = this.budgetRoomLimits || {}
      return fullHouseRevenue({
        headcount: this.fullHouseHeadcount,
        dancers: this.budgetThisYear.dancers,
        cottageLimit: limits.Cottage,
        standardDormLimit: limits['Standard Dorm'],
        adaDormLimit: limits['ADA Dorm']
      })
    },
    fullHouseHint () {
      const h = this.fullHouse
      const parts = []
      if (h.cottages) {
        parts.push(h.cottages + ' cottage × ' + this.fmtMoney(h.cottageListCents))
      }
      if (h.standardDorms) {
        parts.push(h.standardDorms + ' dorm × ' + this.fmtMoney(h.dormListCents))
      }
      if (h.adaDorms) {
        parts.push(h.adaDorms + ' large dorm × ' + this.fmtMoney(h.dormListCents))
      }
      if (h.access) {
        parts.push(h.access + ' access × ' + this.fmtMoney(h.accessListCents))
      }
      if (!parts.length) {
        return 'No tickets'
      }
      return parts.join(' + ')
    },
    fullHouseCancels () {
      return scaleCancelLoss(this.budgetOutlook.cancels, this.fullHouseHeadcount)
    },
    fullHouseProfitCents () {
      return this.fullHouse.packageCents -
        this.fullHouseCancels.remainingLossCents +
        this.budgetOutlook.extras.outlookCents -
        this.forecastOperatingCents
    },
    outlookPredictedRevenueCents () {
      if (this.activeYearEnded) {
        return this.yearTotals.revenue
      }
      return predictedRevenueCents({
        dancerDueCents: this.budgetOutlook.packages.dueCents,
        remainingCancelLossCents: this.budgetOutlook.cancels.remainingLossCents,
        extrasOutlookCents: this.budgetOutlook.extras.outlookCents,
        actualRevenueCents: this.yearTotals.revenue
      })
    },
    predictedProfit () {
      const operatingEac = this.operatingTotals.eacExpense
      if (this.activeYearEnded) {
        return this.yearTotals.revenue - this.yearTotals.operatingExpense
      }
      return this.outlookPredictedRevenueCents - operatingEac
    },
    forecastNetTotalCents () {
      return this.lineRowTotals.forecastNet
    },
    splitSumCents () {
      return (this.editingTx.splits || []).reduce((sum, s) => sum + (Number(s.amountCents) || 0), 0)
    },
    canSave () {
      if (!this.editingTx.date) return false
      const validSplits = (this.editingTx.splits || []).filter(s => s.productLineId && (Number(s.amountCents) || 0) > 0)
      return validSplits.length > 0
    },
    canSync () {
      return !!this.syncFromDate && !!this.syncToDate && this.syncFromDate < this.syncToDate
    },
    deletableRows () {
      // Wipe target: every bank-feed row (regardless of type — catches
      // BROCHE deposits accidentally imported as revenue, etc.) PLUS
      // every expense or transfer from any source — except Stripe fees
      // (which pair with their parent revenue row).
      return (this.transactions || []).filter(t => {
        // Always keep Stripe fees — they belong to their parent charge.
        if (t.source === 'stripe' && String(t.sourceRef || '').endsWith(':fee')) return false
        // Always keep Stripe revenue rows.
        if (t.source === 'stripe' && t.type === 'revenue') return false
        // Everything else from the bank importer goes regardless of type.
        if (t.source === 'bank') return true
        // And catch any straggler expenses or transfers tagged with a
        // different source (manual or no-source entries).
        if (t.type === 'expense' || t.type === 'transfer') return true
        return false
      })
    },
    staleBankRevenueCount () {
      // ANY non-Stripe revenue row whose description matches the
      // transfer regex — leftovers from imports/manual entries
      // before transfer support was added. One click below
      // reclassifies them as transfers.
      return (this.transactions || []).filter(t =>
        t.type === 'revenue' && t.source !== 'stripe' && this.isTransferDescription(t.description)
      ).length
    },
    nonStripeRevenueRows () {
      // The actual transactions making up the non-Stripe revenue
      // total — sorted biggest first so the mystery dollars show up.
      return (this.yearTransactions || [])
        .filter(t => t.type === 'revenue' && t.source !== 'stripe')
        .slice()
        .sort((a, b) => (Number(b.totalCents) || 0) - (Number(a.totalCents) || 0))
    },
    nonStripeRevenueBySource () {
      // Diagnostic: every dollar of revenue that isn't coming from
      // Stripe, grouped by the `source` field. Helps locate where any
      // mystery revenue is hiding (manual entries, old bank imports,
      // store-order callbacks, etc.).
      const map = new Map()
      for (const t of this.yearTransactions) {
        if (t.type !== 'revenue' || t.source === 'stripe') continue
        const key = t.source || '(no source)'
        map.set(key, (map.get(key) || 0) + (Number(t.totalCents) || 0))
      }
      return Array.from(map.entries())
        .map(([source, cents]) => ({ source, cents }))
        .sort((a, b) => b.cents - a.cents)
    },
    nonStripeRevenueTotal () {
      return this.nonStripeRevenueBySource.reduce((s, r) => s + r.cents, 0)
    },
    reconciliation () {
      // Stripe charges go into the BROCHE BALLET LLC account, then
      // BROCHE LLC transfers the net into the IABF Capital One account.
      // For the active festival year:
      //   netStripe = Stripe revenue − Stripe fees (both already in
      //               the ledger via Stripe sync)
      //   brocheIn  = sum of transfer rows whose description matches
      //               an "instant transfer received from BROCHE BALLET"
      //   delta     = netStripe − brocheIn (≈ 0 = reconciled; positive
      //               = money still sitting in BROCHE waiting to move)
      const BROCHE_RX = /broche ballet llc/i
      let stripeRevenue = 0
      let stripeFees = 0
      let brocheIn = 0
      const brocheSeen = new Set()
      for (const t of this.yearLedger) {
        const total = Number(t.totalCents) || 0
        if (t.source === 'stripe' && t.type === 'revenue') stripeRevenue += total
        if (t.source === 'stripe' && t.type === 'expense' && String(t.sourceRef || '').endsWith(':fee')) stripeFees += total
        if (t.type === 'transfer' && BROCHE_RX.test(t.description || '')) {
          const key = String(t.date || '') + '|' + total
          if (brocheSeen.has(key)) continue
          brocheSeen.add(key)
          brocheIn += total
        }
      }
      const netStripe = stripeRevenue - stripeFees
      return {
        stripeRevenue,
        stripeFees,
        netStripe,
        brocheIn,
        delta: netStripe - brocheIn
      }
    },
    lastStripeSyncText () {
      const ts = this.syncMeta && this.syncMeta.lastStripeSyncAt
      if (!ts) return ''
      const d = new Date(ts)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    },
    // How far the ledger actually reaches, which is not the last range synced
    // — a narrow catch-up run leaves that range well behind the data. Take the
    // later of the newest charge on hand and the furthest window ever pulled.
    lastCoveredDate () {
      let latest = ''
      for (const t of this.transactions) {
        if (t.source !== 'stripe') continue
        if (String(t.sourceRef || '').endsWith(':fee')) continue
        const d = String(t.date || '')
        if (d > latest) latest = d
      }
      for (const run of this.syncRuns) {
        const end = Number(run && run.endTimestamp)
        if (!end) continue
        const d = new Date(end * 1000).toISOString().slice(0, 10)
        if (d > latest) latest = d
      }
      return latest
    },
    lastStripeSyncRangeText () {
      const r = this.syncMeta && this.syncMeta.lastStripeSyncRange
      if (!r || !r.startTimestamp) return ''
      return `${this.fmtStamp(r.startTimestamp)} → ${this.fmtStamp(r.endTimestamp)}`
    },
    syncRuns () {
      const runs = this.syncMeta && this.syncMeta.syncRuns
      return Array.isArray(runs) ? runs : []
    },
    lastStripeSyncSummary () {
      const r = this.syncMeta && this.syncMeta.lastStripeSyncResult
      if (!r) return ''
      const parts = [
        `${r.imported || 0} new`,
        `${r.updated || 0} updated`,
        `${r.deleted || 0} deleted`,
        `${r.skipped || 0} filtered out`,
        `of ${r.scanned || 0} scanned`
      ]
      return `(${parts.join(', ')})`
    },
    dismissedStripe () {
      return this.transactions
        .filter(t => t.dismissed && t.source === 'stripe')
        .slice()
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    },
    plaidItems () {
      return (this.plaidStatus && this.plaidStatus.items) || []
    }
  },
  watch: {
    activeYear () {
      this.loadBudgetOutlook()
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const adminYear = Number(this.$store.getters.adminFestivalYear)
    if (Number.isFinite(adminYear) && adminYear > 0) {
      this.activeYear = adminYear
    }
    try {
      this.unsubLines = await this.$store.dispatch('subscribeAccountingProductLines')
      this.unsubTxs = await this.$store.dispatch('subscribeAccountingTransactions')
      this.ensureVenueAssetsLine()
      // Default sync window: last 90 days.
      const now = new Date()
      const ninety = new Date()
      ninety.setDate(now.getDate() - 90)
      this.syncFromDate = ninety.toISOString().slice(0, 10)
      this.syncToDate = now.toISOString().slice(0, 10)
      // Subscribe to sync state + known-amounts docs.
      const { db } = await import('@/firebase')
      this.unsubSyncMeta = db.collection(IABF.accountingMeta).doc('sync-state').onSnapshot(doc => {
        this.syncMeta = doc.exists ? doc.data() : null
      })
      // Festival-year state: { ended: bool, endedAt: ts } per year doc.
      this.unsubYearMeta = db.collection(IABF.accountingMeta).doc('festival-years').onSnapshot(doc => {
        this.yearMetaByYear = doc.exists ? (doc.data() || {}) : {}
      })
      this.unsubKnownAmounts = db.collection(IABF.accountingMeta).doc('known-amounts').onSnapshot(doc => {
        this.knownAmountsDoc = doc.exists ? (doc.data() || {}) : null
        if (this.knownAmountsHydrated || this.snapshotIsUnconfirmedMiss(doc)) return
        this.knownAmountsHydrated = true
        if (doc.exists) {
          const data = doc.data() || {}
          const cents = Array.isArray(data.amountsCents) ? data.amountsCents : []
          this.knownAmountsDollars = cents.map(c => Math.round(Number(c) || 0) / 100)
        } else {
          // Fresh install — seed inputs with the obvious starters so the
          // user just hits blur to save. We don't auto-save here so
          // intentionally-empty stays empty if they delete everything.
          this.knownAmountsDollars = [450, 2200]
        }
      })
      this.unsubExclusionRules = db.collection(IABF.accountingMeta).doc('exclusion-rules').onSnapshot(doc => {
        const DEFAULT_SEEDS = [
          { key: 'v1-25-invoice', amountCents: 2500, descriptionMatch: '^Invoice ', note: 'On-demand platform membership ($25)' },
          { key: 'v2-15-invoice', amountCents: 1500, descriptionMatch: '^Invoice ', note: 'On-demand platform membership ($15)' }
        ]
        this.exclusionRulesDoc = doc.exists ? (doc.data() || {}) : null
        if (this.exclusionRulesHydrated || this.snapshotIsUnconfirmedMiss(doc)) return
        this.exclusionRulesHydrated = true
        if (doc.exists) {
          const data = doc.data() || {}
          const raw = Array.isArray(data.rules) ? data.rules : []
          const seeded = Array.isArray(data.seededDefaults) ? data.seededDefaults : []
          this.exclusionRules = raw.map(r => ({
            amountCents: r.amountCents || 0,
            amountDollars: r.amountCents ? r.amountCents / 100 : null,
            descriptionMatch: r.descriptionMatch || '',
            note: r.note || ''
          }))
          // Append any default seeds we haven't applied yet. Tracked by key
          // so a user-deleted default stays deleted.
          const missing = DEFAULT_SEEDS.filter(s => !seeded.includes(s.key))
          if (missing.length) {
            for (const s of missing) {
              this.exclusionRules.push({
                amountCents: s.amountCents,
                amountDollars: s.amountCents / 100,
                descriptionMatch: s.descriptionMatch,
                note: s.note
              })
            }
            this.saveExclusionRules([...seeded, ...missing.map(s => s.key)])
          }
        } else {
          // Fresh install — seed all defaults and mark them as applied so
          // future versions can add new ones without overwriting deletions.
          this.exclusionRules = DEFAULT_SEEDS.map(s => ({
            amountCents: s.amountCents,
            amountDollars: s.amountCents / 100,
            descriptionMatch: s.descriptionMatch,
            note: s.note
          }))
          this.saveExclusionRules(DEFAULT_SEEDS.map(s => s.key))
        }
      })
      this.unsubCategorizationRules = db.collection(IABF.accountingMeta).doc('categorization-rules').onSnapshot(doc => {
        const DEFAULT_SEEDS = [
          {
            key: 'v1-iabf-payment-' + IABF_DANCER_YEAR,
            amountsCents: [],
            descriptionMatch: '^IABF ' + IABF_DANCER_YEAR + ' Payment',
            productLineName: 'IABF Payment',
            note: 'Festival payments from Stripe receipts'
          }
        ]
        const toLocal = r => ({
          amountsCents: Array.isArray(r.amountsCents) ? r.amountsCents.slice() : [],
          amountsText: Array.isArray(r.amountsCents) ? r.amountsCents.map(c => c / 100).join(', ') : '',
          descriptionMatch: r.descriptionMatch || '',
          productLineName: r.productLineName || '',
          note: r.note || ''
        })
        this.categorizationRulesDoc = doc.exists ? (doc.data() || {}) : null
        if (this.categorizationRulesHydrated || this.snapshotIsUnconfirmedMiss(doc)) return
        this.categorizationRulesHydrated = true
        if (doc.exists) {
          const data = doc.data() || {}
          const raw = Array.isArray(data.rules) ? data.rules : []
          const seeded = Array.isArray(data.seededDefaults) ? data.seededDefaults : []
          this.categorizationRules = raw.map(toLocal)
          const missing = DEFAULT_SEEDS.filter(s => !seeded.includes(s.key))
          if (missing.length) {
            for (const s of missing) this.categorizationRules.push(toLocal(s))
            this.saveCategorizationRules([...seeded, ...missing.map(s => s.key)])
          }
        } else {
          this.categorizationRules = DEFAULT_SEEDS.map(toLocal)
          this.saveCategorizationRules(DEFAULT_SEEDS.map(s => s.key))
        }
      })
    } catch (e) {
      console.error('Accounting subscribe failed:', e)
    }
    this.loading = false
    this.loadPlaidStatus()
    this.resumePlaidOAuth()
    this.loadBudgetOutlook()
  },
  beforeDestroy () {
    if (typeof this.unsubLines === 'function') this.unsubLines()
    if (typeof this.unsubTxs === 'function') this.unsubTxs()
    if (typeof this.unsubSyncMeta === 'function') this.unsubSyncMeta()
    if (typeof this.unsubKnownAmounts === 'function') this.unsubKnownAmounts()
    if (typeof this.unsubExclusionRules === 'function') this.unsubExclusionRules()
    if (typeof this.unsubCategorizationRules === 'function') this.unsubCategorizationRules()
    if (typeof this.unsubYearMeta === 'function') this.unsubYearMeta()
  },
  methods: {
    isAmazonDescription,
    emptyBudgetYear () {
      return { dancers: [], addonServices: [], storeOrders: [], guests: [], canceled: [] }
    },
    fmtRate (rate) {
      const n = Number(rate) || 0
      return (Math.round(n * 1000) / 10) + '%'
    },
    fmtPeople (n) {
      const x = Number(n) || 0
      if (Math.abs(x - Math.round(x)) < 0.05) {
        return String(Math.round(x))
      }
      return (Math.round(x * 10) / 10).toFixed(1)
    },
    vsForecastTitle (row) {
      if (!row || !row.hasForecast) {
        return ''
      }
      const plan = 'Plan net ' + this.fmtMoney(row.forecastNet)
      if (!row.hasActuals) {
        return plan + ' — vs forecast waits until something posts on this line'
      }
      return plan
    },
    cancelPhaseLabel (phase) {
      if (phase === 'ended') {
        return 'No remaining cancel forecast after Jul 1'
      }
      if (phase === 'after-deadline') {
        return 'After Apr 15: 50% of package minus $450 deposit'
      }
      return 'Until Apr 15: full package'
    },
    mapSnapDocs (snap) {
      return ((snap && snap.docs) || []).map(doc => ({ id: doc.id, ...doc.data() }))
    },
    async loadBudgetYear (year) {
      const { db } = await import('@/firebase')
      const cols = iabfForYear(year)
      const dancerCol = dancersCollection(year)
      const canceledNames = []
      if (cols.canceledDancers) {
        canceledNames.push(cols.canceledDancers)
      }
      if (Number(year) === 2026 && cols.canceledDancers !== 'iabf-canceled-dancers') {
        canceledNames.push('iabf-canceled-dancers')
      }
      const getCol = (name) => {
        if (!name) {
          return Promise.resolve({ docs: [] })
        }
        return db.collection(name).get().catch((e) => {
          console.warn('budget year collection failed', name, e)
          return { docs: [] }
        })
      }
      const [dancers, addons, store, guests, ...canceledSnaps] = await Promise.all([
        getCol(dancerCol),
        getCol(cols.addonServices),
        getCol(cols.storeOrders),
        getCol(cols.guestRegistrations)
      ].concat(canceledNames.map(getCol)))
      const canceledById = new Map()
      for (const snap of canceledSnaps) {
        for (const rec of this.mapSnapDocs(snap)) {
          canceledById.set(rec.id, rec)
        }
      }
      return {
        dancers: this.mapSnapDocs(dancers),
        addonServices: this.mapSnapDocs(addons),
        storeOrders: this.mapSnapDocs(store),
        guests: this.mapSnapDocs(guests),
        canceled: Array.from(canceledById.values())
      }
    },
    async loadBudgetOutlook () {
      const year = this.activeYear
      const seq = this.budgetLoadSeq + 1
      this.budgetLoadSeq = seq
      this.budgetLoading = true
      try {
        const { db } = await import('@/firebase')
        const peopleGet = db.collection(IABF.people).get().catch((e) => {
          console.warn('budget outlook people load failed', e)
          return { docs: [] }
        })
        const [thisYear, lastYear, peopleSnap, roomLimits] = await Promise.all([
          this.loadBudgetYear(year),
          this.loadBudgetYear(year - 1),
          peopleGet,
          this.$store.dispatch('loadIabfRoomLimits', year).catch((e) => {
            console.warn('budget outlook room limits load failed', e)
            return { limits: {} }
          })
        ])
        if (seq !== this.budgetLoadSeq) {
          return
        }
        this.budgetThisYear = thisYear
        this.budgetLastYear = lastYear
        this.budgetPeople = this.mapSnapDocs(peopleSnap)
        this.budgetRoomLimits = (roomLimits && roomLimits.limits) || {}
        this.budgetLoadedForYear = year
      } catch (e) {
        console.warn('budget outlook load failed', e)
        if (seq !== this.budgetLoadSeq) {
          return
        }
        this.budgetRoomLimits = {}
        this.budgetLoadedForYear = null
      }
      this.budgetLoading = false
    },
    fmtMoney (cents) {
      const dollars = (Math.round(Number(cents) || 0) / 100)
      return '$' + dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    bankKindLabel (tx) {
      const ref = String((tx && tx.sourceRef) || '')
      if (ref.indexOf('plaid:') === 0) {
        return 'plaid'
      }
      if (ref.indexOf('capitalone:') === 0) {
        return 'csv'
      }
      return ''
    },
    lineNameById (id) {
      const line = this.productLines.find(l => l.id === id)
      return line ? line.name : '(unknown)'
    },
    // Clicking the line again clears it, so the same control both opens and
    // closes the drill-down.
    filterByLine (lineId) {
      this.lineFilterId = this.lineFilterId === lineId ? '' : lineId
      if (!this.lineFilterId) return
      this.$nextTick(() => {
        const el = this.$refs.txSection
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    fmtStamp (unixSeconds) {
      const n = Number(unixSeconds)
      if (!n) return '—'
      // Stripe stamps are unix seconds; render the local calendar date the
      // way the From/To pickers show it.
      const d = new Date(n * 1000)
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' })
    },
    fmtRunTime (ms) {
      const d = new Date(Number(ms) || 0)
      return isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    },
    // The festival cycle runs Aug → Jul, so the active year's window starts
    // 1 Aug of the previous calendar year. Ends today, or 31 Jul if that
    // festival year is already over.
    fmtDay (isoDate) {
      const d = new Date(String(isoDate) + 'T12:00:00')
      return isNaN(d.getTime()) ? isoDate : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    // Everything since the ledger's high-water mark, backed up a couple of
    // days: a charge can be created just before a window closes, and fees and
    // refunds settle after the fact. Re-pulling those days is free — rows are
    // matched on their Stripe id and updated in place.
    async syncLatest () {
      this.syncing = true
      try {
        const { functions } = await import('@/firebase')
        const callable = functions.httpsCallable('syncStripeToAccounting', { timeout: 540000 })
        const result = await callable({ latest: true })
        const data = result.data || {}
        // eslint-disable-next-line no-alert
        window.alert(
          `Stripe sync complete.\n\n` +
          `Scanned ${data.scanned || 0}\n` +
          `New (matched): ${data.imported || 0}\n` +
          `Updated existing (matched): ${data.updated || 0}\n` +
          `Deleted (no longer matches): ${data.deleted || 0}\n` +
          `Filtered out / non-succeeded: ${data.skipped || 0}`
        )
      } catch (e) {
        console.error('Stripe sync failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Sync failed: ' + (e && e.message ? e.message : e))
      }
      this.syncing = false
    },
    setSyncRangeToFestivalYear () {
      const start = `${this.activeYear - 1}-08-01`
      const lastDay = `${this.activeYear}-07-31`
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      this.syncFromDate = start
      this.syncToDate = todayStr < lastDay ? todayStr : lastDay
    },
    festivalYearFromDate (dateStr) {
      const y = parseInt(String(dateStr || '').slice(0, 4), 10)
      const m = parseInt(String(dateStr || '').slice(5, 7), 10)
      if (!isFinite(y) || !isFinite(m)) return new Date().getFullYear()
      return m >= 8 ? y + 1 : y
    },
    feeForTx (tx) {
      if (tx.source !== 'stripe' || !tx.sourceRef) return null
      return this.feeByParentRef.get(tx.sourceRef) || null
    },
    txFestivalYear (tx) {
      // Prefer the stored festivalYear; fall back to a date-derived value
      // for pre-existing transactions that don't have it yet.
      if (Number.isFinite(tx.festivalYear)) return tx.festivalYear
      return this.festivalYearFromDate(tx.date)
    },
    lineIsInvestment (line) {
      if (!line || line.id === '__uncat__') {
        return false
      }
      if (nameLooksLikeInvestment(line.name)) {
        return true
      }
      return line.kind === 'investment'
    },
    lineKind (line) {
      return this.lineIsInvestment(line) ? 'investment' : 'operating'
    },
    setLineKind (line, kind) {
      this.$set(line, 'kind', kind === 'investment' ? 'investment' : 'operating')
      this.onLineFieldBlur(line)
    },
    async rewriteSplitsToLine (fromId, toId) {
      if (!fromId || !toId || fromId === toId) {
        return
      }
      const { db } = await import('@/firebase')
      const COL = db.collection(IABF.accountingTransactions)
      const toUpdate = []
      for (const tx of this.transactions || []) {
        const splits = tx.splits || []
        if (!splits.some(s => s.productLineId === fromId)) {
          continue
        }
        const next = splits.map(s => {
          if (s.productLineId !== fromId) {
            return s
          }
          return { ...s, productLineId: toId }
        })
        toUpdate.push({ id: tx.id, splits: next })
      }
      for (let i = 0; i < toUpdate.length; i += 400) {
        const batch = db.batch()
        for (const row of toUpdate.slice(i, i + 400)) {
          batch.set(COL.doc(row.id), { splits: row.splits, updatedAt: Date.now() }, { merge: true })
        }
        await batch.commit()
      }
    },
    async ensureVenueAssetsLine () {
      if (!this.productLines.length) {
        return
      }
      const key = (name) => String(name || '').trim().toLowerCase()
      const supplies = this.productLines.find(l => key(l.name) === 'venue supplies')
      let assets = this.productLines.find(l => key(l.name).indexOf('venue assets') === 0)
      if (assets && supplies && assets.id !== supplies.id) {
        await this.rewriteSplitsToLine(supplies.id, assets.id)
        await this.$store.dispatch('deleteAccountingProductLine', supplies.id)
        return
      }
      if (supplies && !assets) {
        this.$set(supplies, 'name', VENUE_ASSETS_NAME)
        this.$set(supplies, 'kind', 'investment')
        await this.$store.dispatch('saveAccountingProductLine', supplies)
        return
      }
      if (assets) {
        if (assets.name !== VENUE_ASSETS_NAME || assets.kind !== 'investment') {
          this.$set(assets, 'name', VENUE_ASSETS_NAME)
          this.$set(assets, 'kind', 'investment')
          await this.$store.dispatch('saveAccountingProductLine', assets)
        }
        return
      }
      await this.$store.dispatch('saveAccountingProductLine', {
        name: VENUE_ASSETS_NAME,
        sortOrder: this.productLines.length,
        notes: '',
        kind: 'investment'
      })
    },
    async seedProductLines () {
      this.seeding = true
      try {
        await Promise.all(DEFAULT_LINES.map((name, idx) =>
          this.$store.dispatch('saveAccountingProductLine', {
            name,
            sortOrder: idx,
            notes: '',
            kind: nameLooksLikeInvestment(name) ? 'investment' : 'operating'
          })
        ))
      } catch (e) {
        console.error('seed failed:', e)
      }
      this.seeding = false
    },
    addLineRow () {
      this.$store.dispatch('saveAccountingProductLine', {
        name: 'New line',
        sortOrder: this.productLines.length,
        notes: '',
        kind: 'operating'
      })
    },
    // budgetCents predates the split into a revenue plan and an expense
    // plan; where a line still carries one it counts as the expense plan.
    lineTotalsByIdForYear (year) {
      const map = {}
      for (const t of this.transactions || []) {
        if (!t || t.dismissed) {
          continue
        }
        if (this.txFestivalYear(t) !== year) {
          continue
        }
        for (const s of (t.splits || [])) {
          const id = s.productLineId
          if (!map[id]) {
            map[id] = { revenue: 0, expense: 0 }
          }
          const amt = s.amountCents || 0
          if (t.type === 'revenue') {
            map[id].revenue += amt
          } else if (t.type === 'expense') {
            map[id].expense += amt
          }
        }
      }
      return map
    },
    lineForecastRevenue (line) {
      return this.lineForecastRevenueResolved(line).cents
    },
    lineForecastExpense (line) {
      return this.lineForecastExpenseResolved(line).cents
    },
    lineForecastPrior (line) {
      if (this.lineIsInvestment(line)) {
        return { expense: 0, revenue: 0 }
      }
      return this.priorYearLineTotalsById[line && line.id] || { expense: 0, revenue: 0 }
    },
    lineForecastRevenueResolved (line) {
      const prior = this.lineForecastPrior(line)
      return resolveLineForecastRevenue({
        year: this.activeYear,
        yearEnded: this.activeYearEnded,
        forecastRevenueByYear: line && line.forecastRevenueByYear,
        forecastRevenueCents: line && line.forecastRevenueCents,
        priorYearRevenueCents: Number(prior.revenue) || 0
      })
    },
    lineForecastExpenseResolved (line) {
      const prior = this.lineForecastPrior(line)
      return resolveLineForecastExpense({
        year: this.activeYear,
        yearEnded: this.activeYearEnded,
        forecastExpenseByYear: line && line.forecastExpenseByYear,
        forecastExpenseCents: line && line.forecastExpenseCents,
        budgetCents: line && line.budgetCents,
        priorYearExpenseCents: Number(prior.expense) || 0
      })
    },
    // A line that has never earned anything doesn't need a revenue box sitting
    // empty on every row — but it might still be worth forecasting, so the
    // box appears when you click the dash.
    isForecastCellOpen (row, side) {
      if (row.isUncategorized) return false
      const forecast = side === 'rev' ? row.forecastRevenue : row.forecastExpense
      const actual = side === 'rev' ? row.revenue : row.expense
      if (forecast || actual) return true
      return this.revealedForecastCells.indexOf(side + '-' + row.line.id) !== -1
    },
    revealForecastCell (row, side) {
      const key = side + '-' + row.line.id
      if (this.revealedForecastCells.indexOf(key) === -1) {
        this.revealedForecastCells.push(key)
      }
      this.$nextTick(() => {
        const ref = this.$refs['fc-' + key]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.focus) el.focus()
      })
    },
    onFullHouseHeadcount (raw) {
      const n = Math.round(Number(raw))
      if (!Number.isFinite(n) || n < 1) {
        this.fullHouseHeadcount = DEFAULT_FULL_HOUSE_HEADCOUNT
        return
      }
      this.fullHouseHeadcount = Math.min(200, n)
    },
    forecastCellKey (row, side) {
      return side + '-' + row.line.id
    },
    forecastField (side) {
      return side === 'rev' ? 'forecastRevenueCents' : 'forecastExpenseCents'
    },
    forecastInputTitle (row, side) {
      const source = side === 'rev' ? row.forecastRevenueSource : row.forecastExpenseSource
      if (source === 'prior-actual') {
        return 'Carried from ' + (this.activeYear - 1) + ' actuals — type to set a ' + this.activeYear + ' plan, or clear to keep last year'
      }
      if (source === 'typed') {
        return 'This year’s plan. Clear the box or hit reset to go back to last year’s actuals'
      }
      return ''
    },
    forecastInputValue (row, side) {
      const key = this.forecastCellKey(row, side)
      if (Object.prototype.hasOwnProperty.call(this.forecastDrafts, key)) {
        return this.forecastDrafts[key]
      }
      const cents = side === 'rev' ? row.forecastRevenue : row.forecastExpense
      const source = side === 'rev' ? row.forecastRevenueSource : row.forecastExpenseSource
      if (source === 'typed') {
        return String((Number(cents) || 0) / 100)
      }
      if (cents) {
        return String(cents / 100)
      }
      return ''
    },
    unsetForecastYear (line, field) {
      const mapField = field === 'forecastExpenseCents' ? 'forecastExpenseByYear' : 'forecastRevenueByYear'
      const next = Object.assign({}, line[mapField] || {})
      this.$delete(next, String(this.activeYear))
      this.$set(line, mapField, next)
      this.$set(line, field, 0)
      if (field === 'forecastExpenseCents') {
        this.$set(line, 'budgetCents', 0)
      }
    },
    applyForecastParsed (line, field, parsed) {
      if (parsed.unset) {
        this.unsetForecastYear(line, field)
        return
      }
      this.$set(line, field, parsed.cents)
      const mapField = field === 'forecastExpenseCents' ? 'forecastExpenseByYear' : 'forecastRevenueByYear'
      const next = Object.assign({}, line[mapField] || {})
      next[String(this.activeYear)] = parsed.cents
      this.$set(line, mapField, next)
    },
    onForecastDraftInput (row, side, raw) {
      const key = this.forecastCellKey(row, side)
      this.$set(this.forecastDrafts, key, raw)
      this.applyForecastParsed(row.line, this.forecastField(side), parseForecastInput(raw))
    },
    onForecastBlur (row, side) {
      const key = this.forecastCellKey(row, side)
      const draft = this.forecastDrafts[key]
      this.$delete(this.forecastDrafts, key)
      if (draft !== undefined) {
        this.applyForecastParsed(row.line, this.forecastField(side), parseForecastInput(draft))
      }
      this.onLineFieldBlur(row.line)
    },
    resetForecast (row, side) {
      const key = this.forecastCellKey(row, side)
      this.$delete(this.forecastDrafts, key)
      this.unsetForecastYear(row.line, this.forecastField(side))
      this.onLineFieldBlur(row.line)
    },
    onLineFieldBlur (line) {
      if (!line.id) return
      const kind = this.lineKind(line)
      if (line.kind !== kind) {
        this.$set(line, 'kind', kind)
      }
      this.$store.dispatch('saveAccountingProductLine', line)
    },
    async confirmDeleteLine (line) {
      if (!line.id) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete product line "${line.name}"? Transactions referencing it will keep the id but show as "(unknown)".`)) return
      await this.$store.dispatch('deleteAccountingProductLine', line.id)
    },
    openNew () {
      this.editingTx = emptyTx(this.activeYear)
      this.dialogError = ''
      this.dialogOpen = true
    },
    openEdit (tx) {
      // Clone so the dialog edits a local copy.
      const dollarSplits = (tx.splits || []).map(s => ({
        productLineId: s.productLineId,
        amountCents: s.amountCents,
        amountDollars: (s.amountCents || 0) / 100,
        note: s.note || ''
      }))
      this.editingTx = {
        id: tx.id,
        date: tx.date,
        festivalYear: Number.isFinite(tx.festivalYear) ? tx.festivalYear : null,
        type: tx.type,
        source: tx.source || 'manual',
        sourceRef: tx.sourceRef || '',
        description: tx.description || '',
        splits: dollarSplits.length ? dollarSplits : [emptySplit()],
        notes: tx.notes || ''
      }
      this.dialogError = ''
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    addSplit () {
      this.editingTx.splits.push(emptySplit())
    },
    removeSplit (i) {
      this.editingTx.splits.splice(i, 1)
    },
    onSplitAmountInput (split) {
      const dollars = Number(split.amountDollars)
      split.amountCents = isFinite(dollars) ? Math.round(dollars * 100) : 0
    },
    async onSave () {
      if (!this.canSave) return
      this.saving = true
      this.dialogError = ''
      try {
        await this.$store.dispatch('saveAccountingTransaction', this.editingTx)
        this.dialogOpen = false
      } catch (e) {
        console.error('save failed:', e)
        this.dialogError = 'Save failed: ' + (e && e.message ? e.message : e)
      }
      this.saving = false
    },

    isTransferDescription (desc) {
      // JPMorgan ext transfers stay as expenses by default; user can
      // override per row. CC payments come in lots of flavors —
      // "CAPITAL ONE MOBILE PYMT", "CAPITAL ONE AUTOPAY PYMT",
      // "CAPITAL ONE ONLINE PMT", etc. — so we catch any
      // "Capital One … PYMT/PMT" string.
      const TRANSFER_RX = /(withdrawal (to|from) 360 (checking|performance savings)|capital one|payment\/credit|deposit from 360 (checking|performance savings)|instant transfer received from broche|broche ballet)/i
      return TRANSFER_RX.test(String(desc || ''))
    },
    async toggleEndFestivalYear () {
      const y = String(this.activeYear)
      const isEnded = this.activeYearEnded
      const verb = isEnded ? 'Reopen' : 'End'
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`${verb} festival ${y}?\n\n` + (isEnded
        ? 'Reopening switches forecasts back to max(Budget, Actual) — under-budget lines stop counting as savings until you close again.'
        : 'Ending switches the per-line forecast from max(Budget, Actual) to Actual. Under-budget savings drop straight into Predicted Profit.'))
      if (!ok) return
      this.endingYear = true
      try {
        const { db } = await import('@/firebase')
        const next = { ...(this.yearMetaByYear || {}) }
        next[y] = isEnded
          ? { ended: false, endedAt: null, reopenedAt: Date.now() }
          : { ended: true, endedAt: Date.now() }
        await db.collection(IABF.accountingMeta).doc('festival-years').set(next, { merge: true })
      } catch (e) {
        console.error('toggleEndFestivalYear failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Failed: ' + (e && e.message ? e.message : e))
      }
      this.endingYear = false
    },
    async wipeAllExpenses () {
      const targets = this.deletableRows
      if (!targets.length) return
      const byType = { expense: 0, transfer: 0, revenue: 0, other: 0 }
      for (const t of targets) {
        if (t.type === 'expense') byType.expense += 1
        else if (t.type === 'transfer') byType.transfer += 1
        else if (t.type === 'revenue') byType.revenue += 1
        else byType.other += 1
      }
      const dollars = targets.reduce((s, t) => s + (Number(t.totalCents) || 0), 0) / 100
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Delete ${targets.length} row(s) totaling $${dollars.toFixed(2)}?\n\n· ${byType.expense} expense row(s)\n· ${byType.transfer} transfer row(s) (BROCHE deposits, CC payments, inter-account moves)\n· ${byType.revenue} bank-tagged revenue row(s) (misclassified non-Stripe revenue)\n${byType.other ? '· ' + byType.other + ' other row(s)\n' : ''}\nStripe revenue + Stripe fees are KEPT. After this, Plaid can re-sync from scratch.\n\nThis is destructive — there's no undo.`)
      if (!ok) return
      this.wipingExpenses = true
      this.wipeProgress = ''
      try {
        let done = 0
        for (const t of targets) {
          await this.$store.dispatch('deleteAccountingTransaction', t.id)
          done += 1
          this.wipeProgress = `Deleted ${done} / ${targets.length}`
        }
        this.wipeProgress = ''
      } catch (e) {
        console.error('wipe expenses failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Wipe failed: ' + (e && e.message ? e.message : e))
      }
      this.wipingExpenses = false
    },
    async reclassifyBankRevenueAsTransfers () {
      const stale = (this.transactions || []).filter(t =>
        t.type === 'revenue' && t.source !== 'stripe' && this.isTransferDescription(t.description)
      )
      if (!stale.length) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Reclassify ${stale.length} bank revenue row(s) as transfers?\n\nThis flips the type to "transfer" and clears the product-line splits. They'll stop counting in revenue and start showing in the reconciliation panel. Safe to re-run.`)) return
      this.reclassifyRunning = true
      try {
        for (const t of stale) {
          await this.$store.dispatch('saveAccountingTransaction', {
            id: t.id,
            date: t.date,
            festivalYear: t.festivalYear,
            type: 'transfer',
            source: t.source,
            sourceRef: t.sourceRef,
            description: t.description,
            splits: [],
            totalCents: t.totalCents,
            notes: t.notes
          })
        }
      } catch (e) {
        console.error('reclassify failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Reclassify failed: ' + (e && e.message ? e.message : e))
      }
      this.reclassifyRunning = false
    },
    async onDelete () {
      const isStripe = (this.editingTx.source === 'stripe')
      const prompt = isStripe
        ? 'Dismiss this Stripe-imported transaction? It will hide from the dashboard but stay in the database so the next Stripe sync still skips it. (If you fully delete it, the next sync over the same date range will re-import it.)'
        : 'Delete this transaction?'
      // eslint-disable-next-line no-alert
      if (!window.confirm(prompt)) return
      this.deleting = true
      try {
        if (isStripe) {
          await this.$store.dispatch('saveAccountingTransaction', { ...this.editingTx, dismissed: true })
          await this.setFeeRowDismissed(this.editingTx, true)
        } else {
          await this.$store.dispatch('deleteAccountingTransaction', this.editingTx.id)
        }
        this.dialogOpen = false
      } catch (e) {
        console.error('delete failed:', e)
        this.dialogError = 'Action failed: ' + (e && e.message ? e.message : e)
      }
      this.deleting = false
    },
    confirmDelete (tx) {
      const isStripe = (tx.source === 'stripe')
      const prompt = isStripe
        ? 'Dismiss this Stripe-imported transaction? It will hide from the dashboard but stay in the database so the next Stripe sync still skips it.'
        : 'Delete this transaction?'
      // eslint-disable-next-line no-alert
      if (!this.skipDeleteConfirm && !window.confirm(prompt)) return
      if (isStripe) {
        this.$store.dispatch('saveAccountingTransaction', { ...tx, dismissed: true })
          .then(() => this.setFeeRowDismissed(tx, true))
          .catch(e => console.error('dismiss failed:', e))
      } else {
        this.$store.dispatch('deleteAccountingTransaction', tx.id)
      }
    },
    // The Stripe fee row is hidden from the list and rendered inline on its
    // charge, so there's no way to act on it directly — it has to follow
    // whatever happens to the charge, or a dismissed charge leaves its fee
    // behind counting as expense.
    async setFeeRowDismissed (tx, dismissed) {
      const fee = this.feeForTx(tx)
      if (!fee || !fee.id || !!fee.dismissed === dismissed) return
      try {
        const { db } = await import('@/firebase')
        await db.collection(IABF.accountingTransactions).doc(fee.id).set({
          dismissed,
          updatedAt: Date.now()
        }, { merge: true })
      } catch (e) {
        console.error('fee row dismiss failed:', e)
      }
    },
    addKnownAmount () {
      this.knownAmountsDollars.push(0)
    },
    removeKnownAmount (i) {
      this.knownAmountsDollars.splice(i, 1)
      this.saveKnownAmounts()
    },
    async saveKnownAmounts () {
      const cents = this.knownAmountsDollars
        .map(v => Math.round((Number(v) || 0) * 100))
        .filter(c => c > 0)
      // Dedupe and sort.
      const set = new Set(cents)
      const sorted = Array.from(set).sort((a, b) => a - b)
      try {
        const { db } = await import('@/firebase')
        await db.collection(IABF.accountingMeta).doc('known-amounts').set({
          amountsCents: sorted,
          updatedAt: Date.now()
        }, { merge: true })
      } catch (e) {
        console.error('saveKnownAmounts failed:', e)
      }
    },
    // First categorization of a row that came in with no splits: the whole
    // total goes to the chosen line.
    async inlineSetProductLine (tx, productLineId) {
      if (!productLineId) return
      const splits = [{
        productLineId,
        amountCents: Math.round(Number(tx.totalCents) || 0),
        note: ''
      }]
      try {
        await this.$store.dispatch('saveAccountingTransaction', { ...tx, splits })
      } catch (e) {
        console.error('inline categorize failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save: ' + (e && e.message ? e.message : e))
      }
    },
    async inlineChangeProductLine (tx, splitIndex, newProductLineId) {
      const splits = (tx.splits || []).map((s, i) => i === splitIndex
        ? { ...s, productLineId: newProductLineId }
        : s
      )
      try {
        await this.$store.dispatch('saveAccountingTransaction', { ...tx, splits })
      } catch (e) {
        console.error('inline change failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save: ' + (e && e.message ? e.message : e))
      }
    },
    addExclusionRule () {
      this.exclusionRules.push({ amountCents: 0, amountDollars: null, descriptionMatch: '', note: '' })
    },
    removeExclusionRule (i) {
      this.exclusionRules.splice(i, 1)
      this.saveExclusionRules()
    },
    normalizeExclusionRule (r) {
      return {
        amountCents: Math.round((Number(r.amountDollars) || 0) * 100),
        descriptionMatch: String(r.descriptionMatch || '').trim(),
        note: String(r.note || '').trim()
      }
    },
    // A rule with neither an amount nor a description would exclude every
    // charge, so it isn't persisted — it stays on screen, flagged, until
    // it's filled in.
    exclusionRuleIsSavable (r) {
      const rule = this.normalizeExclusionRule(r)
      return rule.amountCents > 0 || !!rule.descriptionMatch
    },
    // Mirrors chargeIsExcluded() in the sync function: a blank amount or a
    // blank regex means "any", and a charge is excluded when both sides agree.
    compileExclusionRule (r) {
      const rule = this.normalizeExclusionRule(r)
      if (!rule.amountCents && !rule.descriptionMatch) {
        return { error: 'This rule needs an amount or a description regex — as written it would exclude every transaction.' }
      }
      let descRx = null
      if (rule.descriptionMatch) {
        try {
          descRx = new RegExp(rule.descriptionMatch, 'i')
        } catch (e) {
          return { error: `"${rule.descriptionMatch}" isn't a valid regex: ${e.message}` }
        }
      }
      return { compiled: { amountCents: rule.amountCents, descRx, note: rule.note } }
    },
    exclusionRuleMatchesTx (compiled, tx) {
      const amountOk = compiled.amountCents === 0 ||
        compiled.amountCents === Math.round(Number(tx.totalCents) || 0)
      const descOk = !compiled.descRx || compiled.descRx.test(String(tx.description || ''))
      return amountOk && descOk
    },
    async applyExclusionRuleHistorically (r) {
      const result = this.compileExclusionRule(r)
      if (result.error) {
        // eslint-disable-next-line no-alert
        window.alert(result.error)
        return
      }
      const compiled = result.compiled
      // Fee rows never match on their own — their amount and description are
      // the fee's, not the charge's. They ride along with their parent below,
      // the same way the sync deletes a fee doc when it drops the charge.
      const matched = this.yearLedger.filter(t =>
        !(t.source === 'stripe' && String(t.sourceRef || '').endsWith(':fee')) &&
        this.exclusionRuleMatchesTx(compiled, t)
      )
      const fromStripe = matched.filter(t => t.source === 'stripe')
      // The sync leaves hand-edited rows alone when a rule would exclude
      // them, so this does too.
      const toDismiss = fromStripe.filter(t => !t.userEdited)
      const handEdited = fromStripe.length - toDismiss.length
      const notFromStripe = matched.length - fromStripe.length
      const feeRows = toDismiss
        .map(t => this.feeByParentRef.get(String(t.sourceRef || '')))
        .filter(f => f && !f.dismissed && !f.userEdited)

      if (!matched.length) {
        // eslint-disable-next-line no-alert
        window.alert(`Nothing in festival year ${this.activeYear} matches this rule.`)
        return
      }
      if (!toDismiss.length) {
        // eslint-disable-next-line no-alert
        window.alert(
          `Matched ${matched.length} transaction${matched.length === 1 ? '' : 's'} in festival year ${this.activeYear}, ` +
          'but none of them can be dismissed — they\'re either hand-edited or not from Stripe.'
        )
        return
      }

      const criteria = []
      if (compiled.amountCents) {
        criteria.push(this.fmtMoney(compiled.amountCents))
      }
      if (compiled.descRx) {
        criteria.push(`/${compiled.descRx.source}/i`)
      }
      const lines = [
        'Exclude these from the ledger?',
        '',
        `Rule: ${criteria.join(' + ')}`,
        `Festival year ${this.activeYear}`,
        '',
        `Matched ${matched.length} transaction${matched.length === 1 ? '' : 's'}`,
        `  • ${toDismiss.length} Stripe charge${toDismiss.length === 1 ? '' : 's'} → dismissed` +
          (feeRows.length ? ` (plus ${feeRows.length} fee row${feeRows.length === 1 ? '' : 's'})` : '')
      ]
      if (handEdited > 0) {
        lines.push(`  • ${handEdited} hand-edited → left alone`)
      }
      if (notFromStripe > 0) {
        lines.push(`  • ${notFromStripe} not from Stripe → left alone`)
      }
      lines.push(
        '',
        'Dismissed rows drop out of every total and the next Stripe sync deletes',
        'them for good. Until then you can undo from the auto-dismissed list.',
        '',
        `Dismiss ${toDismiss.length + feeRows.length} row${toDismiss.length + feeRows.length === 1 ? '' : 's'}?`
      )
      // eslint-disable-next-line no-alert
      if (!window.confirm(lines.join('\n'))) return

      this.applyingRule = true
      try {
        const { db } = await import('@/firebase')
        const COL = db.collection(IABF.accountingTransactions)
        // Deliberately not setting userEdited: that's the flag telling the
        // sync to keep its hands off, and it would stop the sync ever
        // clearing these rows out properly.
        const all = [...toDismiss, ...feeRows]
        const CHUNK = 400
        for (let i = 0; i < all.length; i += CHUNK) {
          const batch = db.batch()
          for (const tx of all.slice(i, i + CHUNK)) {
            batch.set(COL.doc(tx.id), { dismissed: true, updatedAt: Date.now() }, { merge: true })
          }
          await batch.commit()
        }
        // eslint-disable-next-line no-alert
        window.alert(
          `Dismissed ${toDismiss.length} charge${toDismiss.length === 1 ? '' : 's'}` +
          (feeRows.length ? ` and ${feeRows.length} fee row${feeRows.length === 1 ? '' : 's'}` : '') + '.'
        )
      } catch (e) {
        console.error('applyExclusionRuleHistorically failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not apply the rule: ' + (e && e.message ? e.message : e))
      }
      this.applyingRule = false
    },
    async saveExclusionRules (seededDefaults) {
      const rules = this.exclusionRules
        .filter(r => this.exclusionRuleIsSavable(r))
        .map(r => this.normalizeExclusionRule(r))
      try {
        const { db } = await import('@/firebase')
        const payload = { rules, updatedAt: Date.now() }
        if (Array.isArray(seededDefaults)) payload.seededDefaults = seededDefaults
        await db.collection(IABF.accountingMeta).doc('exclusion-rules').set(payload, { merge: true })
      } catch (e) {
        console.error('saveExclusionRules failed:', e)
      }
    },
    addCategorizationRule () {
      this.categorizationRules.push({
        amountsCents: [],
        amountsText: '',
        descriptionMatch: '',
        productLineName: '',
        note: ''
      })
    },
    removeCategorizationRule (i) {
      this.categorizationRules.splice(i, 1)
      this.saveCategorizationRules()
    },
    normalizeCategorizationRule (r) {
      const amountsCents = String(r.amountsText || '')
        .split(/[,;\s]+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => Math.round((parseFloat(s) || 0) * 100))
        .filter(n => n > 0)
      return {
        amountsCents,
        descriptionMatch: String(r.descriptionMatch || '').trim(),
        productLineName: String(r.productLineName || '').trim(),
        note: String(r.note || '').trim()
      }
    },
    // A cache-only "doesn't exist" is a cache miss, not an answer. Since the
    // rule editors hydrate from the first snapshot they trust, seeding off
    // one would overwrite real saved rules with defaults — wait for the
    // server's word instead.
    snapshotIsUnconfirmedMiss (doc) {
      return !doc.exists && !!(doc.metadata && doc.metadata.fromCache)
    },
    // Stripe rows carry the charge id in sourceRef (fee rows use the same id
    // with a ":fee" suffix), which is all the Dashboard needs to open the
    // payment. Bank and manual rows have nothing to link to.
    stripeUrlFor (tx) {
      if (tx.source !== 'stripe') return ''
      const id = String(tx.sourceRef || '').replace(/:fee$/, '')
      if (!/^(ch|pi|py)_[A-Za-z0-9]+$/.test(id)) return ''
      return `https://dashboard.stripe.com/payments/${id}`
    },
    productLineIdByName (name) {
      const key = String(name || '').toLowerCase().trim()
      const line = this.productLines.find(l => String(l.name || '').toLowerCase().trim() === key)
      return line ? line.id : ''
    },
    // Turn an editor row into something we can match transactions against,
    // or explain why we can't. Mirrors categorizationLineFor() in the sync
    // function so "apply to past" files rows exactly where a re-import would.
    compileCategorizationRule (r) {
      const rule = this.normalizeCategorizationRule(r)
      if (!rule.productLineName) {
        return { error: 'This rule needs a product line before it can be applied.' }
      }
      if (!rule.amountsCents.length && !rule.descriptionMatch) {
        return { error: 'This rule needs an amount or a description regex — as written it would match every transaction.' }
      }
      const productLineId = this.productLineIdByName(rule.productLineName)
      if (!productLineId) {
        return {
          error: `No product line named "${rule.productLineName}" exists yet.\n\n` +
            'The Stripe sync creates it on the next import. To apply the rule to past rows now, ' +
            'add the line first under "Edit product lines".'
        }
      }
      let descRx = null
      if (rule.descriptionMatch) {
        try {
          descRx = new RegExp(rule.descriptionMatch, 'i')
        } catch (e) {
          return { error: `"${rule.descriptionMatch}" isn't a valid regex: ${e.message}` }
        }
      }
      return { compiled: { productLineId, productLineName: rule.productLineName, amountsCents: rule.amountsCents, descRx } }
    },
    categorizationRuleMatchesTx (compiled, tx) {
      const amountOk = compiled.amountsCents.length === 0 ||
        compiled.amountsCents.includes(Math.round(Number(tx.totalCents) || 0))
      const descOk = !compiled.descRx || compiled.descRx.test(String(tx.description || ''))
      return amountOk && descOk
    },
    // "Uncategorized" = nothing has claimed the row yet: no splits at all, or
    // a single split that's blank or parked in "Needs categorizing". Rows
    // deliberately split across lines are left alone — a rule assigns one
    // line to the whole amount and would flatten them.
    txIsUncategorized (tx) {
      const splits = tx.splits || []
      if (splits.length === 0) return true
      if (splits.length > 1) return false
      const id = splits[0].productLineId
      return !id || id === this.productLineIdByName('Needs categorizing')
    },
    async applyCategorizationRuleHistorically (r) {
      const result = this.compileCategorizationRule(r)
      if (result.error) {
        // eslint-disable-next-line no-alert
        window.alert(result.error)
        return
      }
      const compiled = result.compiled
      // Stripe fee rows always belong on the fee line and transfers carry no
      // product line by design — the sync never rule-categorizes either, so
      // this doesn't. Dismissed rows are the reject pile; leave them there.
      const matched = this.transactions.filter(tx =>
        this.txFestivalYear(tx) === this.activeYear &&
        tx.type !== 'transfer' &&
        !tx.dismissed &&
        !(tx.source === 'stripe' && String(tx.sourceRef || '').endsWith(':fee')) &&
        this.categorizationRuleMatchesTx(compiled, tx)
      )
      const toChange = matched.filter(tx => this.txIsUncategorized(tx))
      const multiSplit = matched.filter(tx => (tx.splits || []).length > 1).length
      const alreadyFiled = matched.length - toChange.length - multiSplit

      if (!matched.length) {
        // eslint-disable-next-line no-alert
        window.alert(`Nothing in festival year ${this.activeYear} matches this rule.`)
        return
      }
      if (!toChange.length) {
        // eslint-disable-next-line no-alert
        window.alert(
          `Matched ${matched.length} transaction${matched.length === 1 ? '' : 's'} in festival year ${this.activeYear}, ` +
          'but they\'re all filed already — nothing to change.'
        )
        return
      }

      const criteria = []
      if (compiled.amountsCents.length) {
        criteria.push(compiled.amountsCents.map(c => this.fmtMoney(c)).join(', '))
      }
      if (compiled.descRx) {
        criteria.push(`/${compiled.descRx.source}/i`)
      }
      const lines = [
        'Apply this rule to past transactions?',
        '',
        `Rule: ${criteria.join(' + ')} → ${compiled.productLineName}`,
        `Festival year ${this.activeYear}`,
        '',
        `Matched ${matched.length} transaction${matched.length === 1 ? '' : 's'}`,
        `  • ${toChange.length} uncategorized → will change`
      ]
      if (alreadyFiled > 0) {
        lines.push(`  • ${alreadyFiled} already filed elsewhere → skipped`)
      }
      if (multiSplit > 0) {
        lines.push(`  • ${multiSplit} split across lines → skipped`)
      }
      lines.push('', `Apply to ${toChange.length} row${toChange.length === 1 ? '' : 's'}?`)
      // eslint-disable-next-line no-alert
      if (!window.confirm(lines.join('\n'))) return

      this.applyingRule = true
      try {
        const { db } = await import('@/firebase')
        const COL = db.collection(IABF.accountingTransactions)
        // Deliberately not setting userEdited: these rows stay rule-driven,
        // so a later sync over the same range reaches the same answer instead
        // of freezing a bulk edit in place.
        const CHUNK = 400
        for (let i = 0; i < toChange.length; i += CHUNK) {
          const batch = db.batch()
          for (const tx of toChange.slice(i, i + CHUNK)) {
            batch.set(COL.doc(tx.id), {
              splits: [{
                productLineId: compiled.productLineId,
                amountCents: Math.round(Number(tx.totalCents) || 0),
                note: ''
              }],
              updatedAt: Date.now()
            }, { merge: true })
          }
          await batch.commit()
        }
        // eslint-disable-next-line no-alert
        window.alert(`Filed ${toChange.length} transaction${toChange.length === 1 ? '' : 's'} under "${compiled.productLineName}".`)
      } catch (e) {
        console.error('applyCategorizationRuleHistorically failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not apply the rule: ' + (e && e.message ? e.message : e))
      }
      this.applyingRule = false
    },
    // Requires a product line plus at least one of (amount, description) —
    // otherwise the rule would claim every charge. Half-typed rows stay on
    // screen, flagged, instead of being written.
    categorizationRuleIsSavable (r) {
      const rule = this.normalizeCategorizationRule(r)
      return !!rule.productLineName && (rule.amountsCents.length > 0 || !!rule.descriptionMatch)
    },
    async saveCategorizationRules (seededDefaults) {
      const rules = this.categorizationRules
        .filter(r => this.categorizationRuleIsSavable(r))
        .map(r => this.normalizeCategorizationRule(r))
      try {
        const { db } = await import('@/firebase')
        const payload = { rules, updatedAt: Date.now() }
        if (Array.isArray(seededDefaults)) payload.seededDefaults = seededDefaults
        await db.collection(IABF.accountingMeta).doc('categorization-rules').set(payload, { merge: true })
      } catch (e) {
        console.error('saveCategorizationRules failed:', e)
      }
    },
    async undismiss (tx) {
      try {
        await this.$store.dispatch('saveAccountingTransaction', { ...tx, dismissed: false })
        await this.setFeeRowDismissed(tx, false)
      } catch (e) {
        console.error('undismiss failed:', e)
      }
    },
    async runStripeSync () {
      if (!this.canSync) return
      this.syncing = true
      try {
        const { functions } = await import('@/firebase')
        // Server-side timeout is 540s; the SDK default client timeout is
        // 70s which causes the browser to give up while the function is
        // still running. Match the server.
        const callable = functions.httpsCallable('syncStripeToAccounting', { timeout: 540000 })
        // Unix-second timestamps for Stripe.
        const startTs = Math.floor(new Date(this.syncFromDate + 'T00:00:00').getTime() / 1000)
        const endTs = Math.floor(new Date(this.syncToDate + 'T23:59:59').getTime() / 1000)
        const result = await callable({ startTimestamp: startTs, endTimestamp: endTs })
        const data = result.data || {}
        // eslint-disable-next-line no-alert
        window.alert(
          `Stripe sync complete.\n\n` +
          `Scanned ${data.scanned || 0}\n` +
          `New (matched): ${data.imported || 0}\n` +
          `Updated existing (matched): ${data.updated || 0}\n` +
          `Deleted (no longer matches): ${data.deleted || 0}\n` +
          `Filtered out / non-succeeded: ${data.skipped || 0}\n\n` +
          `Match rule: charge has a known dancer email, OR no email at all + a known amount. Manual edits via the dialog are preserved on re-sync.`
        )
      } catch (e) {
        console.error('Stripe sync failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Sync failed: ' + (e && e.message ? e.message : e))
      }
      this.syncing = false
    },
    plaidRedirectUri () {
      if (typeof window === 'undefined') {
        return ''
      }
      // Plaid: HTTPS, no query string, no hash. Must match an Allowed redirect URI.
      return window.location.origin + window.location.pathname
    },
    storePlaidLinkToken (token) {
      try {
        window.localStorage.setItem('iabf_plaid_link_token', token)
      } catch (e) { /* private mode */ }
    },
    readPlaidLinkToken () {
      try {
        return window.localStorage.getItem('iabf_plaid_link_token') || ''
      } catch (e) {
        return ''
      }
    },
    clearPlaidLinkToken () {
      try {
        window.localStorage.removeItem('iabf_plaid_link_token')
      } catch (e) { /* private mode */ }
    },
    loadPlaidScript () {
      return new Promise((resolve, reject) => {
        if (window.Plaid) {
          resolve(window.Plaid)
          return
        }
        const existing = document.querySelector('script[data-plaid-link]')
        if (existing) {
          existing.addEventListener('load', () => resolve(window.Plaid))
          existing.addEventListener('error', () => reject(new Error('Could not load Plaid Link')))
          return
        }
        const script = document.createElement('script')
        script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'
        script.async = true
        script.setAttribute('data-plaid-link', '1')
        script.onload = () => resolve(window.Plaid)
        script.onerror = () => reject(new Error('Could not load Plaid Link'))
        document.head.appendChild(script)
      })
    },
    async loadPlaidStatus () {
      try {
        const { functions } = await import('@/firebase')
        const result = await functions.httpsCallable('getPlaidStatus')()
        this.plaidStatus = result.data || null
      } catch (e) {
        const msg = (e && e.message) || ''
        if (/not found|unimplemented/i.test(msg)) {
          this.plaidStatus = { configured: false, items: [] }
          return
        }
        this.plaidError = msg || 'Could not load Capital One connection.'
      }
    },
    openPlaidHandler (linkToken, receivedRedirectUri) {
      return new Promise(async (resolve, reject) => {
        try {
          const Plaid = await this.loadPlaidScript()
          const handler = Plaid.create({
            token: linkToken,
            receivedRedirectUri: receivedRedirectUri || undefined,
            onSuccess: (publicToken, metadata) => resolve({ publicToken, metadata }),
            onExit: (err) => {
              if (err) {
                reject(err)
              } else {
                resolve(null)
              }
            }
          })
          handler.open()
        } catch (e) {
          reject(e)
        }
      })
    },
    async connectPlaid () {
      this.plaidBusy = true
      this.plaidError = ''
      this.plaidNotice = ''
      try {
        const { functions } = await import('@/firebase')
        const tokenRes = await functions.httpsCallable('createPlaidLinkToken')({
          redirectUri: this.plaidRedirectUri()
        })
        const linkToken = tokenRes.data && tokenRes.data.linkToken
        if (!linkToken) {
          throw new Error('Plaid did not return a link token.')
        }
        this.storePlaidLinkToken(linkToken)
        const result = await this.openPlaidHandler(linkToken)
        if (!result || !result.publicToken) {
          this.plaidBusy = false
          return
        }
        await functions.httpsCallable('exchangePlaidPublicToken')({
          publicToken: result.publicToken
        })
        await this.loadPlaidStatus()
        this.plaidNotice = 'Connected. Syncing posted transactions…'
        await this.syncPlaid()
      } catch (e) {
        console.error('connectPlaid failed:', e)
        this.plaidError = (e && e.message) || 'Could not connect Capital One.'
      }
      this.plaidBusy = false
    },
    async resumePlaidOAuth () {
      if (typeof window === 'undefined') {
        return
      }
      const params = new URLSearchParams(window.location.search)
      if (!params.get('oauth_state_id')) {
        return
      }
      this.plaidBusy = true
      this.plaidError = ''
      try {
        const { functions } = await import('@/firebase')
        // Must reuse the same Link token from the first open, not mint a new one.
        const linkToken = this.readPlaidLinkToken()
        if (!linkToken) {
          throw new Error('Plaid login came back, but the original Link token is missing. Try Connect again.')
        }
        const result = await this.openPlaidHandler(linkToken, window.location.href)
        this.clearPlaidLinkToken()
        if (result && result.publicToken) {
          await functions.httpsCallable('exchangePlaidPublicToken')({
            publicToken: result.publicToken
          })
          await this.loadPlaidStatus()
          await this.syncPlaid()
        }
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, window.location.pathname)
        }
      } catch (e) {
        console.error('resumePlaidOAuth failed:', e)
        this.plaidError = (e && e.message) || 'Could not finish Capital One login.'
      }
      this.plaidBusy = false
    },
    async syncPlaid (itemId) {
      this.plaidBusy = true
      this.plaidError = ''
      try {
        const { functions } = await import('@/firebase')
        const result = await functions.httpsCallable('syncPlaidTransactions', { timeout: 540000 })({
          itemId: itemId || ''
        })
        const data = result.data || {}
        this.plaidNotice = `Imported ${data.added || 0} new row(s); skipped ${data.skipped || 0}.`
        await this.loadPlaidStatus()
      } catch (e) {
        console.error('syncPlaid failed:', e)
        this.plaidError = (e && e.message) || 'Capital One sync failed.'
      }
      this.plaidBusy = false
    },
    async unlinkPlaid (itemId) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Disconnect this Capital One login? Existing imported rows stay in the ledger.')) {
        return
      }
      this.plaidBusy = true
      this.plaidError = ''
      try {
        const { functions } = await import('@/firebase')
        const result = await functions.httpsCallable('unlinkPlaidItem')({ itemId })
        this.plaidStatus = result.data || this.plaidStatus
        this.plaidNotice = 'Disconnected.'
      } catch (e) {
        console.error('unlinkPlaid failed:', e)
        this.plaidError = (e && e.message) || 'Could not disconnect.'
      }
      this.plaidBusy = false
    }
  }
}
</script>

<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

h2 {
  font-family: Quattrocento, Georgia, serif;
  font-size: 28px;
  margin: 0;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.year-select {
  font: inherit;
  padding: 6px 10px;
  border: 1px solid #d0cbc4;
  border-radius: 4px;
  background: #fff;
}
.year-label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #555;
}
.year-label span { margin-bottom: 2px; }

.muted { color: #888; font-style: italic; }
.small { font-size: 13px; }

.sync-panel {
  background: #fbf8f3;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 14px 0;
}
.sync-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.sync-controls {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.sync-field {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #555;
}
.sync-field input {
  font: inherit;
  font-size: 13px;
  padding: 4px 8px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
}
.sync-foot {
  margin-top: 8px;
  line-height: 1.5;
}
.plaid-accounts {
  margin: 8px 0;
}
.plaid-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #eee8dc;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.reconcile-panel {
  margin: 12px 0;
  padding: 12px 16px;
  background: #f7f5ef;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
}
.reconcile-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.reconcile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}
.recon-stat {
  background: #fff;
  border-radius: 4px;
  padding: 8px 10px;
  border: 1px solid #eee;
}
.recon-stat-strong { background: #faf8f3; border-color: #d8d3c2; }
.recon-stat-delta { font-weight: 700; }
.recon-stat.recon-ok { background: #d4edda; border-color: #b6dcbf; }
.recon-stat.recon-warn { background: #fff3cd; border-color: #ffe7a0; }
.recon-label { font-size: 12px; color: #555; font-family: 'Fanwood Text', Georgia, serif; }
.recon-num { font-family: 'Fanwood Text', Georgia, serif; font-size: 18px; color: #1a1a1a; }
.recon-num.neg { color: #c0392b; }
.recon-foot {
  margin-top: 6px;
  line-height: 1.5;
}

.line-uncat td {
  background: #fff8e1;
  font-style: italic;
}
.line-over td { background: #fdecec; }
.forecast-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}
.forecast-input {
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  font: inherit;
  font-size: 13px;
  padding: 2px 4px;
  text-align: right;
  width: 92px;
}
.forecast-reset {
  background: none;
  border: none;
  color: #9a9488;
  cursor: pointer;
  padding: 2px;
  line-height: 0;
  border-radius: 10px;
}
.forecast-reset:hover {
  color: #267E7D;
  background: #eef6f6;
}
.forecast-input.forecast-inherited {
  background: #f4f7ee;
  border-style: dashed;
}
.forecast-input:focus { border-color: #267E7D; outline: none; }
.forecast-add {
  background: none;
  border: 1px solid transparent;
  border-radius: 3px;
  color: #b9b3a6;
  cursor: pointer;
  font: inherit;
  padding: 2px 10px;
}
.forecast-add:hover { border-color: #267E7D; color: #267E7D; }
.line-total-row td {
  border-top: 2px solid #e6e1d4;
  font-weight: 600;
}
.line-under td { color: #155724; }
.line-note { max-width: 240px; }

.summary-tile-strong {
  background: #faf8f3;
  border: 2px solid #267E7D;
}

.year-close-bar {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.line-edit-head {
  display: grid;
  grid-template-columns: 1.6fr 60px 110px 1.4fr 30px;
  gap: 6px;
  margin-bottom: 4px;
  padding: 0 4px;
}

.recon-cleanup {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff4e6;
  border: 1px solid #f0c890;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.recon-diag {
  background: #f0f4f7;
  border-color: #c9d6df;
  flex-direction: column;
  align-items: stretch;
}
.recon-diag summary {
  cursor: pointer;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}
.nsr-table {
  margin-top: 10px;
  font-size: 13px;
}
.nsr-table th, .nsr-table td {
  padding: 4px 8px;
}
.nsr-pill {
  background: #fff;
  border: 1px solid #d8d3c2;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
}
.nsr-pill code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  background: transparent;
  margin-right: 4px;
}

.danger-zone {
  margin: 12px 0;
  padding: 10px 14px;
  background: #fdf3f3;
  border: 1px solid #f0c0c0;
  border-radius: 6px;
}
.danger-zone summary {
  cursor: pointer;
  font-weight: 700;
  color: #8a1f1f;
}
.danger-zone p {
  margin-top: 8px;
  line-height: 1.5;
}
.danger-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 6px;
}

.known-amounts, .dismissed-block {
  background: #fff;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 10px 14px;
  margin: 8px 0;
}
.known-amounts summary, .dismissed-block summary {
  cursor: pointer;
  font-weight: 700;
  font-family: Quattrocento, Georgia, serif;
}
.known-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}
.known-prefix { color: #555; }
.known-input {
  font: inherit;
  padding: 4px 8px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  width: 130px;
}

.exclusion-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}
.exclusion-head, .exclusion-row {
  display: grid;
  grid-template-columns: 130px 1fr 1fr 104px 32px;
  gap: 6px;
  align-items: center;
}
.cat-head, .cat-row {
  display: grid;
  grid-template-columns: 180px 1fr 160px 1fr 104px 32px;
  gap: 6px;
  align-items: center;
}
.backfill-btn {
  background: #fff;
  border: 1px solid #267E7D;
  border-radius: 3px;
  color: #267E7D;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  padding: 4px 6px;
  white-space: nowrap;
}
.backfill-btn:hover:not(:disabled) { background: #eef6f6; }
.backfill-btn:disabled {
  border-color: #d0cbc4;
  color: #a9a49c;
  cursor: default;
}
.exclusion-head { padding: 0 4px; }
select.rule-input { background: #fff; }
.rule-unsaved input, .rule-unsaved select {
  border-color: #d9a441;
  background: #fdf8ee;
}
.rule-hint { margin-top: 6px; }
.rule-input {
  font: inherit;
  padding: 4px 8px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
}
.dismissed-desc { font-size: 13px; color: #555; }
.link-btn {
  background: none;
  border: none;
  color: #267E7D;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  padding: 0 4px;
  text-decoration: underline;
}
.link-btn:hover { color: #2f9695; }
.amazon-orders-link {
  display: inline-block;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #267E7D;
  text-decoration: none;
  white-space: nowrap;
}
.amazon-orders-link:hover { text-decoration: underline; }
.amazon-orders-link--block {
  display: inline-block;
  margin: 6px 0 0;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin: 16px 0 24px;
}
.summary-tile {
  background: #fbfaf6;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 12px 14px;
}
.tile-label { font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.04em; }
.tile-num { font-family: Quattrocento, serif; font-size: 22px; font-weight: 700; margin-top: 4px; }
.tile-num.pos { color: #2e7d32; }
.tile-num.neg { color: #c0392b; }
.tile-num.neutral { color: #1a1a1a; }
.tile-sub { margin-top: 4px; }

.budget-outlook {
  margin: -8px 0 24px;
}
.outlook-table {
  width: 100%;
  max-width: 860px;
  border-collapse: collapse;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
}
.outlook-table td {
  padding: 6px 10px 6px 0;
  vertical-align: top;
  border-bottom: 1px solid #f1eee9;
}
.outlook-table .outlook-total td {
  font-weight: 700;
  border-bottom: none;
  padding-top: 10px;
}
.outlook-extras {
  margin-top: 8px;
}
.full-house-h {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  font-weight: 600;
  margin: 18px 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.full-house-n {
  width: 64px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  padding: 2px 6px;
  text-align: center;
}
.full-house-n:focus {
  border-color: #267E7D;
  outline: none;
}
.outlook-extras summary {
  cursor: pointer;
}

.tools-fold {
  margin: 12px 0 18px;
  background: #fbf8f3;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 8px 14px 12px;
}
.tools-fold > summary {
  cursor: pointer;
  font-weight: 700;
  padding: 6px 0 10px;
}
.uncat-top {
  margin: 8px 0 20px;
}
.uncat-table {
  max-height: none;
}
.section-h {
  font-family: Quattrocento, Georgia, serif;
  font-size: 18px;
  margin: 24px 0 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
}

.ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
}
.ledger-table th, .ledger-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #f1eee9;
  vertical-align: top;
}
.ledger-table th {
  background: #f7f5ef;
  font-family: Quattrocento, Georgia, serif;
  font-size: 12px;
  letter-spacing: 0.04em;
}
.num-col { text-align: right; white-space: nowrap; }
.num-col.pos { color: #2e7d32; }
.num-col.neg { color: #c0392b; }

.tx-row td { cursor: pointer; }
.tx-row td.split-col, .tx-row td.actions-col { cursor: default; }
.tx-row:hover { background: #fbfaf6; }
.split-col { min-width: 200px; }
.fee-inline {
  font-size: 11px;
  color: #c0392b;
  font-style: italic;
  margin-top: 2px;
  font-weight: 400;
}
.split-amounts > div { line-height: 1.6; }
.actions-col { white-space: nowrap; }
.inline-line-select {
  display: block;
  width: 100%;
  font: inherit;
  font-size: 13px;
  padding: 3px 6px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  background: #fff;
  margin-bottom: 2px;
}
.inline-line-select.needs-cat {
  background: #fff4e5;
  border-color: #f4c63d;
  color: #8a4a00;
  font-weight: 600;
}
.type-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 2px 7px;
  border-radius: 8px;
  text-transform: uppercase;
}
.type-pill.revenue { background: #e0f2f1; color: #00695c; }
.type-pill.expense { background: #fff3e0; color: #c0392b; }

.lines-editor {
  background: #fbfaf6;
  border: 1px dashed #d0cbc4;
  border-radius: 6px;
  padding: 14px;
  margin: 12px 0 24px;
}
.line-section-row td.line-section {
  background: #f3efe6;
  font-family: Quattrocento, Georgia, serif;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5a5548;
  padding-top: 12px;
}
.line-edit {
  display: grid;
  grid-template-columns: 1.6fr 60px 110px 1.4fr 30px;
  gap: 6px;
  margin-bottom: 6px;
  align-items: center;
}
.line-input, .line-order, .line-notes, .line-kind {
  font: inherit;
  font-size: 13px;
  padding: 4px 8px;
  border: 1px solid #e6e1d4;
  border-radius: 3px;
  background: #fff;
}

.icon-btn {
  background: transparent;
  border: 1px solid transparent;
  color: #c0392b;
  cursor: pointer;
  font-size: 14px;
  padding: 0 6px;
}
.icon-btn:hover { background: #fff0f0; border-color: #f4c63d; }
.stripe-link {
  color: #267E7D;
  display: inline-block;
  text-decoration: none;
}
.stripe-link:hover { background: #eef6f6; border-color: #267E7D; }

.sync-history {
  border-top: 1px solid #e6e1d4;
  margin-top: 8px;
  padding-top: 8px;
}
.sync-history summary {
  cursor: pointer;
  font-size: 13px;
}
.sync-history-table {
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
  width: 100%;
}
.sync-history-table th, .sync-history-table td {
  border-bottom: 1px solid #efeade;
  padding: 3px 8px 3px 0;
  text-align: left;
}
.sync-history-table th { color: #6b6659; font-weight: 600; }

.tx-filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 8px 0 10px;
}
.tx-filter {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  gap: 6px;
  font-size: 13px;
}
.line-name-btn {
  background: none;
  border: none;
  border-bottom: 1px dotted #b9b3a6;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
}
.line-name-btn:hover { color: #267E7D; border-bottom-color: #267E7D; }
.line-name-btn.is-filtered { color: #267E7D; font-weight: 600; }
.line-filter-chip {
  align-items: center;
  background: #eef6f6;
  border: 1px solid #267E7D;
  border-radius: 3px;
  display: inline-flex;
  font-size: 13px;
  gap: 6px;
  margin: 8px 0 0;
  padding: 4px 6px 4px 10px;
}
.chip-clear {
  background: none;
  border: none;
  color: #267E7D;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 0 4px;
}
.tx-filter-armed.is-armed {
  background: #fdecec;
  border: 1px solid #c0392b;
  border-radius: 3px;
  color: #c0392b;
  font-weight: 600;
  padding: 2px 8px;
}
.tx-search {
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  font: inherit;
  font-size: 13px;
  min-width: 300px;
  padding: 5px 9px;
}
.search-elsewhere .link-btn + .link-btn::before {
  color: #6b6659;
  content: ', ';
  text-decoration: none;
}

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.dialog-card {
  background: #fff;
  border-radius: 8px;
  padding: 22px 24px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}
.dialog-card h3 {
  font-family: Quattrocento, Georgia, serif;
  font-size: 22px;
  margin: 0 0 12px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.form-field {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #555;
}
.form-field.full { width: 100%; margin-bottom: 10px; }
.form-note {
  flex-basis: 100%;
  margin: -4px 0 4px;
}
.form-field span { margin-bottom: 3px; }
.form-field input, .form-field select, .form-field textarea {
  font: inherit;
  font-size: 14px;
  padding: 6px 10px;
  border: 1px solid #d0cbc4;
  border-radius: 4px;
  background: #fff;
}

.splits-block {
  background: #fbfaf6;
  border: 1px solid #eee5d6;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.splits-header { display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px; }
.split-row {
  display: grid;
  grid-template-columns: 2fr 110px 2fr auto;
  gap: 6px;
  margin-bottom: 6px;
}
.split-line, .split-amount, .split-note {
  font: inherit;
  font-size: 13px;
  padding: 5px 8px;
  border: 1px solid #d0cbc4;
  border-radius: 3px;
  background: #fff;
}
.split-total { margin-top: 8px; font-family: Quattrocount, Georgia, serif; }

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.error-text {
  color: #c0392b;
  margin-top: 6px;
  font-size: 13px;
}
</style>
</content>
</invoke>